/*
 * PB Hall of Fame (storefront)
 * ------------------------------------------------------------------
 * Vanilla JS, no build step, no dependencies. Cloudflare Turnstile is
 * loaded lazily when step 5 of the submit popup opens.
 *
 * Data comes from JSON blocks rendered by sections/hall-of-fame.liquid:
 *   [data-hof="catches"]  public, whitelisted catch_report fields (Active only)
 *   [data-hof="venues"]   catch_venue entries
 *   [data-hof="config"]   proxy path, Turnstile site key, money format
 *   [data-hof="customer"] logged-in customer's own name/email (prefill only)
 */
(() => {
  'use strict';

  /* ================================================================
   * Constants
   * ============================================================== */
  const DRAFT_KEY = 'hof-draft-v1';
  const SOURCE_KEY = 'hof-source-v1';
  const MAX_PHOTOS = 3;
  const MAX_EDGE = 2400;
  const JPEG_QUALITY = 0.85;
  const VENUE_RADIUS_KM = 3;
  const MAX_STORY = 1000;
  const MAX_PRODUCTS = 10;
  const DRAFT_MAX_AGE_MS = 30 * 24 * 3600 * 1000;
  const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const STEP_TITLES = ['Photos', 'The fish', 'Where and when', 'Your setup', 'About you'];
  const SPECIES_ORDER = ['Common', 'Mirror', 'Linear', 'Fully scaled', 'Leather', 'Ghost', 'Grass carp', 'Catfish', 'Other'];
  const WEIGHT_BUCKETS = [
    ['', 'Any weight'],
    ['0-20', 'Under 20lb'],
    ['20-30', '20lb to 30lb'],
    ['30-40', '30lb to 40lb'],
    ['40-50', '40lb to 50lb'],
    ['50-', '50lb and over'],
  ];

  // Which step holds each submit field (used to jump to a server-side error).
  const FIELD_STEPS = {
    photos: 1,
    species: 2, weight_lb: 2, weight_oz: 2, weight: 2, is_pb: 2, fish_name: 2,
    venue_id: 3, venue: 3, venue_free_text: 3, lake_name: 3, catch_date: 3, catch_time: 3, session_length: 3, swim_peg: 3,
    products: 4, variants: 4, rig: 4, hook_size: 4, hookbait_type: 4, bait_brand: 4, bait_flavour: 4,
    feeding_approach: 4, spot_feature: 4, distance_m: 4, fish_in_session: 4, other_gear: 4, story: 4,
    first_name: 5, last_name: 5, email: 5, instagram: 5, home_area: 5, experience_level: 5,
    consent_website: 5, consent_ads: 5, consent_marketing: 5, consent_age: 5, turnstile_token: 5, website: 5,
  };
  // Server field keys that share an error slot with another field.
  const ERROR_ALIASES = { weight_oz: 'weight_lb', weight: 'weight_lb', venue: 'venue_id', variants: 'products' };

  const ICON_CLOSE = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  const ICON_PREV = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_NEXT = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_SHARE = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12 3v12M7 8l5-5 5 5M5 13v7h14v-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const ICON_FISH = '<svg viewBox="0 0 64 40" width="64" height="40" aria-hidden="true" focusable="false"><path d="M4 20c8-12 26-16 40-8l10-8v32l-10-8C30 36 12 32 4 20z" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round"/><circle cx="16" cy="18" r="2" fill="currentColor"/></svg>';

  /* ================================================================
   * Small helpers
   * ============================================================== */
  const qs = (sel, ctx) => (ctx || document).querySelector(sel);
  const qsa = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));
  const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC_MAP[c]);
  const str = (s) => (s == null ? '' : String(s)).trim();
  const debounce = (fn, ms) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
  };
  const rafThrottle = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; fn(); });
    };
  };
  let uidCounter = 0;
  const uid = (prefix) => `${prefix || 'hof'}-${++uidCounter}-${Math.random().toString(36).slice(2, 7)}`;
  const reducedMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const store = (kind) => ({
    get(key) { try { return window[kind].getItem(key); } catch (e) { return null; } },
    set(key, val) { try { window[kind].setItem(key, val); } catch (e) { /* quota / private mode */ } },
    remove(key) { try { window[kind].removeItem(key); } catch (e) { /* ignore */ } },
  });
  const local = store('localStorage');
  const session = store('sessionStorage');

  function readJSON(root, name) {
    const el = qs(`script[data-hof="${name}"]`, root);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch (err) {
      console.error(`[Hall of Fame] Could not parse ${name} JSON`, err);
      return null;
    }
  }

  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s || '';
    const t = document.createElement('textarea');
    t.innerHTML = s;
    return t.value;
  }

  function toGid(id, type) {
    const s = str(id);
    if (!s) return '';
    if (s.indexOf('gid://') === 0) return s;
    return /^\d+$/.test(s) ? `gid://shopify/${type}/${s}` : s;
  }
  const numericId = (id) => {
    const m = /(\d+)\s*$/.exec(str(id));
    return m ? m[1] : '';
  };

  function fmtWeight(oz) {
    const total = Math.max(0, Math.round(Number(oz) || 0));
    const lb = Math.floor(total / 16);
    const rem = total % 16;
    return rem ? `${lb}lb ${rem}oz` : `${lb}lb`;
  }

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  function fmtDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return '';
    return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] || ''} ${m[1]}`;
  }

  const pad = (n) => String(n).padStart(2, '0');
  const localISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  function londonISO() {
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
      const get = (t) => (parts.find((p) => p.type === t) || {}).value;
      return `${get('year')}-${get('month')}-${get('day')}`;
    } catch (e) {
      return localISO(new Date());
    }
  }
  // "Today" for the future-date check: the later of device date and UK date,
  // so nobody abroad gets a false "date is in the future".
  function latestToday() {
    const a = localISO(new Date());
    const b = londonISO();
    return a > b ? a : b;
  }

  // Season (UK meteorological): Spring Mar-May, Summer Jun-Aug, Autumn Sep-Nov, Winter Dec-Feb.
  function seasonFor(iso) {
    const m = /^(\d{4})-(\d{2})/.exec(iso || '');
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    if (mo >= 3 && mo <= 5) return { name: 'Spring', start: `${y}-03-01`, label: `Spring ${y}` };
    if (mo >= 6 && mo <= 8) return { name: 'Summer', start: `${y}-06-01`, label: `Summer ${y}` };
    if (mo >= 9 && mo <= 11) return { name: 'Autumn', start: `${y}-09-01`, label: `Autumn ${y}` };
    const wy = mo === 12 ? y : y - 1;
    return { name: 'Winter', start: `${wy}-12-01`, label: `Winter ${wy}/${String(wy + 1).slice(2)}` };
  }

  function formatMoney(cents, format) {
    const value = Math.round(Number(cents) || 0);
    const fmt = format || '\u00a3{{amount}}';
    const num = (dec, thousands, decimal) => {
      const fixed = (value / 100).toFixed(dec);
      const [int, frac] = fixed.split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return num(0, ',', '.');
        case 'amount_with_comma_separator': return num(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return num(0, '.', ',');
        case 'amount_with_apostrophe_separator': return num(2, "'", '.');
        case 'amount_with_space_separator': return num(2, ' ', ',');
        default: return num(2, ',', '.');
      }
    });
  }

  // Shopify CDN URLs accept a width param.
  function sizedImg(url, width) {
    if (!url) return '';
    const u = String(url);
    if (!/cdn\.shopify|\/cdn\/shop\//.test(u)) return u;
    if (/[?&]width=\d+/.test(u)) return u.replace(/([?&])width=\d+/, `$1width=${width}`);
    return u + (u.indexOf('?') === -1 ? '?' : '&') + `width=${width}`;
  }

  const norm = (s) => String(s || '')
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  function haversineKm(lat1, lng1, lat2, lng2) {
    const R = 6371;
    const rad = (d) => (d * Math.PI) / 180;
    const dLat = rad(lat2 - lat1);
    const dLng = rad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
  }

  function instagramHandle(raw) {
    return str(raw)
      .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
      .replace(/[/?#].*$/, '')
      .replace(/^@+/, '')
      .trim();
  }

  /* ================================================================
   * Traffic source: utm_* + referrer, captured once per browser session
   * ============================================================== */
  function captureSource() {
    try {
      const params = new URLSearchParams(window.location.search);
      const parts = [];
      params.forEach((value, key) => {
        if (/^utm_/i.test(key) && value) parts.push(`${key.slice(4).toLowerCase()}=${value.slice(0, 60)}`);
      });
      const existing = session.get(SOURCE_KEY);
      if (existing && !parts.length) return; // keep first touch unless new campaign params arrive
      let ref = '';
      if (document.referrer) {
        try {
          const r = new URL(document.referrer);
          ref = r.host === window.location.host ? 'site' : r.host;
        } catch (e) { /* ignore */ }
      }
      if (ref) parts.push(`ref=${ref}`);
      session.set(SOURCE_KEY, (parts.join('|') || 'direct').slice(0, 250));
    } catch (e) { /* ignore */ }
  }
  const getSource = () => session.get(SOURCE_KEY) || (document.referrer ? `ref=${document.referrer.slice(0, 200)}` : 'direct');

  /* ================================================================
   * EXIF reader (JPEG only): DateTimeOriginal + GPS
   * ============================================================== */
  function readBlobBuffer(blob) {
    if (blob.arrayBuffer) return blob.arrayBuffer();
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.readAsArrayBuffer(blob);
    });
  }

  function readExif(buffer) {
    try {
      const v = new DataView(buffer);
      if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return null; // not a JPEG
      let off = 2;
      while (off + 4 <= v.byteLength) {
        const marker = v.getUint16(off);
        if ((marker & 0xff00) !== 0xff00) return null;
        if (marker === 0xffda || marker === 0xffd9) return null; // start of scan / end: no EXIF
        const len = v.getUint16(off + 2);
        if (marker === 0xffe1 && off + 10 <= v.byteLength &&
            v.getUint32(off + 4) === 0x45786966 && v.getUint16(off + 8) === 0) { // "Exif\0\0"
          return parseTiff(v, off + 10);
        }
        off += 2 + len;
      }
    } catch (e) { /* malformed EXIF: ignore */ }
    return null;
  }

  function parseTiff(v, t) {
    const order = v.getUint16(t);
    const le = order === 0x4949; // "II" little endian, "MM" big endian
    if (!le && order !== 0x4d4d) return null;
    const u16 = (o) => v.getUint16(t + o, le);
    const u32 = (o) => v.getUint32(t + o, le);
    if (u16(2) !== 42) return null;
    const TYPE_SIZE = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };

    const readIfd = (o) => {
      const tags = {};
      if (!o || t + o + 2 > v.byteLength) return tags;
      const n = u16(o);
      for (let i = 0; i < n; i++) {
        const e = o + 2 + i * 12;
        if (t + e + 12 > v.byteLength) break;
        tags[u16(e)] = { type: u16(e + 2), count: u32(e + 4), at: e + 8 };
      }
      return tags;
    };
    const dataOffset = (tag) => ((TYPE_SIZE[tag.type] || 1) * tag.count > 4 ? u32(tag.at) : tag.at);
    const ascii = (tag) => {
      if (!tag || tag.type !== 2) return '';
      const o = dataOffset(tag);
      let s = '';
      for (let i = 0; i < tag.count && t + o + i < v.byteLength; i++) {
        const c = v.getUint8(t + o + i);
        if (!c) break;
        s += String.fromCharCode(c);
      }
      return s.trim();
    };
    const rationals = (tag) => {
      if (!tag || (tag.type !== 5 && tag.type !== 10)) return null;
      const o = dataOffset(tag);
      const out = [];
      for (let i = 0; i < tag.count; i++) {
        const num = u32(o + i * 8);
        const den = u32(o + i * 8 + 4);
        out.push(den ? num / den : 0);
      }
      return out;
    };

    const ifd0 = readIfd(u32(4));
    const exif = ifd0[0x8769] ? readIfd(u32(ifd0[0x8769].at)) : {};
    const gps = ifd0[0x8825] ? readIfd(u32(ifd0[0x8825].at)) : {};
    const result = {};

    const dt = ascii(exif[0x9003]) || ascii(exif[0x9004]) || ascii(ifd0[0x0132]);
    const m = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(dt);
    if (m && m[1] !== '0000') {
      result.date = `${m[1]}-${m[2]}-${m[3]}`;
      result.time = `${m[4]}:${m[5]}`;
      result.iso = `${result.date}T${m[4]}:${m[5]}:${m[6] || '00'}`;
    }

    const lat = rationals(gps[2]);
    const lng = rationals(gps[4]);
    if (lat && lng && lat.length >= 3 && lng.length >= 3) {
      let la = lat[0] + lat[1] / 60 + lat[2] / 3600;
      let lo = lng[0] + lng[1] / 60 + lng[2] / 3600;
      if (ascii(gps[1]).toUpperCase() === 'S') la = -la;
      if (ascii(gps[3]).toUpperCase() === 'W') lo = -lo;
      if (Number.isFinite(la) && Number.isFinite(lo) && !(la === 0 && lo === 0) && Math.abs(la) <= 90 && Math.abs(lo) <= 180) {
        result.gps = { lat: la, lng: lo };
      }
    }
    return result;
  }

  /* ================================================================
   * Image re-encode (strips ALL metadata) + staged upload
   * ============================================================== */
  const isHeic = (file) => /\.(heic|heif)$/i.test(file.name || '') || /hei[cf]/i.test(file.type || '');

  function loadImage(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => resolve({ img, url });
      img.onerror = () => { URL.revokeObjectURL(url); reject(Object.assign(new Error('decode'), { code: 'decode' })); };
      img.src = url;
    });
  }

  // Draws into a canvas (browsers apply EXIF orientation when drawing an <img>),
  // max MAX_EDGE on the long side, JPEG at JPEG_QUALITY. The result has no EXIF/GPS.
  async function reencodeImage(file) {
    const { img, url } = await loadImage(file);
    try {
      const w0 = img.naturalWidth;
      const h0 = img.naturalHeight;
      if (!w0 || !h0) throw Object.assign(new Error('decode'), { code: 'decode' });
      const scale = Math.min(1, MAX_EDGE / Math.max(w0, h0));
      const w = Math.max(1, Math.round(w0 * scale));
      const h = Math.max(1, Math.round(h0 * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff'; // flatten PNG transparency
      ctx.fillRect(0, 0, w, h);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
      canvas.width = 0; // free memory on iOS
      canvas.height = 0;
      if (!blob) throw Object.assign(new Error('encode'), { code: 'encode' });
      return blob;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // POST multipart to the staged upload target: all parameters first, file last.
  function uploadToTarget(target, blob, filename, onProgress, onXhr) {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      (target.parameters || []).forEach((p) => fd.append(p.name, p.value));
      fd.append('file', blob, filename);
      const xhr = new XMLHttpRequest();
      if (onXhr) onXhr(xhr);
      xhr.open('POST', target.url);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`Upload failed (${xhr.status}). Please try again.`));
      };
      xhr.onerror = () => reject(new Error('Upload failed. Check your connection and try again.'));
      xhr.onabort = () => reject(Object.assign(new Error('aborted'), { aborted: true }));
      xhr.send(fd);
    });
  }

  /* ================================================================
   * Cart: add to cart + hand off to Impact's cart drawer
   * ============================================================== */
  const shopRoot = () => {
    const r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.endsWith('/') ? r : `${r}/`;
  };

  /**
   * Adds a variant to the cart and opens Impact's cart drawer.
   *
   * ASSUMPTIONS about Impact (Maestrooo) - verify against the theme's JS and adjust here only:
   *  1. Impact refreshes its cart drawer + header cart count when `cart:refresh` is dispatched
   *     on document.documentElement.
   *  2. Impact's own product forms dispatch `variant:add` on document.documentElement with
   *     detail { items, cart } after /cart/add.js; the cart drawer listens for it (and may open
   *     itself when "open cart on add" is enabled). We mimic that event exactly.
   *  3. The drawer is a <cart-drawer> custom element with a show() method (Impact's dialog base
   *     class). If there is no drawer (cart type = page), we navigate to /cart.
   * `onDrawer(drawerEl)` is called before the drawer opens so our popup can step aside.
   */
  async function addToCart(variantId, quantity, onDrawer) {
    const root = shopRoot();
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] }),
    });
    let added = null;
    try { added = await res.json(); } catch (e) { added = null; }
    if (!res.ok) {
      throw new Error((added && (added.description || added.message)) || 'Could not add this to your cart.');
    }

    let cart = null;
    try {
      cart = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
    } catch (e) { cart = null; }

    const items = added && Array.isArray(added.items) ? added.items : (added ? [added] : []);
    const html = document.documentElement;
    html.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items, cart } }));
    html.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (onDrawer) onDrawer(drawer);
      if (typeof drawer.show === 'function') {
        try { drawer.show(); } catch (e) { /* theme may already be opening it via variant:add */ }
      }
      return { drawer: true, cart };
    }
    window.location.href = `${root}cart`;
    return { drawer: false, cart };
  }

  /* ================================================================
   * Modal (focus trap, Esc, scroll lock)
   * ============================================================== */
  const openModals = [];
  let scrollLocks = 0;
  function lockScroll(on) {
    scrollLocks = Math.max(0, scrollLocks + (on ? 1 : -1));
    document.documentElement.classList.toggle('hof-scroll-lock', scrollLocks > 0);
  }
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  class Modal {
    constructor(el) {
      this.el = el;
      this.panel = qs('.hof-modal__panel', el);
      this.isOpen = false;
      this.yielding = false;
      this.onRequestClose = null;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      el.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-hof-close]');
        if (btn && el.contains(btn)) {
          e.preventDefault();
          this.requestClose();
        }
      });
      el.addEventListener('pointerdown', () => this.unyield());
    }

    requestClose() {
      if (this.onRequestClose) this.onRequestClose();
      else this.close();
    }

    open(opener) {
      if (this.isOpen) return;
      this.isOpen = true;
      this.returnFocus = opener || document.activeElement;
      this.el.hidden = false;
      void this.el.offsetWidth; // reflow so the transition runs
      this.el.classList.add('is-open');
      lockScroll(true);
      openModals.push(this);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('focusin', this.handleFocusIn);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.unyield();
      this.el.classList.remove('is-open');
      this.el.hidden = true;
      lockScroll(false);
      const i = openModals.indexOf(this);
      if (i > -1) openModals.splice(i, 1);
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('focusin', this.handleFocusIn);
      const rf = this.returnFocus;
      this.returnFocus = null;
      if (rf && rf.isConnected && typeof rf.focus === 'function') {
        try { rf.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      }
    }

    focus(target) {
      const el = target || this.panel;
      try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    }

    isTop() { return openModals[openModals.length - 1] === this; }

    focusables() {
      return qsa(FOCUSABLE, this.panel).filter((n) => n.getClientRects().length > 0 && !n.closest('[hidden], [inert]'));
    }

    handleKeydown(e) {
      if (!this.isTop() || this.yielding) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (e.defaultPrevented) return; // e.g. a combobox closed its list
        const a = document.activeElement;
        if (!a || a === document.body || this.el.contains(a)) {
          e.preventDefault();
          this.requestClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const f = this.focusables();
      if (!f.length) { e.preventDefault(); this.focus(); return; }
      const first = f[0];
      const last = f[f.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === this.panel || !this.el.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !this.el.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }

    handleFocusIn(e) {
      if (!this.isTop() || this.yielding) return;
      if (!this.el.contains(e.target)) {
        const f = this.focusables();
        this.focus(f[0]);
      }
    }

    // Step aside (lower z-index, pause focus trap) while the theme's cart drawer is open.
    yieldTo(other) {
      this.yielding = true;
      this.el.classList.add('is-yielding');
      if (this.observer) this.observer.disconnect();
      if (other && window.MutationObserver) {
        let seenOpen = other.hasAttribute('open');
        this.observer = new MutationObserver(() => {
          if (other.hasAttribute('open')) seenOpen = true;
          else if (seenOpen) this.unyield();
        });
        this.observer.observe(other, { attributes: true, attributeFilter: ['open'] });
      }
    }

    unyield() {
      if (!this.yielding) return;
      this.yielding = false;
      this.el.classList.remove('is-yielding');
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
    }
  }

  /* ================================================================
   * Combobox (type-ahead listbox), used for venues and products
   * ============================================================== */
  class Combo {
    constructor({ input, list, search, render, onPick, minChars = 1, delay = 0, emptyText = 'No matches' }) {
      this.input = input;
      this.list = list;
      this.search = search;
      this.render = render;
      this.onPick = onPick;
      this.minChars = minChars;
      this.emptyText = emptyText;
      this.items = [];
      this.active = -1;
      this.seq = 0;
      if (!list.id) list.id = uid('hof-list');
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', list.id);
      list.setAttribute('role', 'listbox');

      const run = () => this.run();
      const later = delay ? debounce(run, delay) : run;
      input.addEventListener('input', later);
      input.addEventListener('focus', () => { if (input.value.trim().length >= this.minChars) run(); });
      input.addEventListener('keydown', (e) => this.onKey(e));
      input.addEventListener('blur', () => setTimeout(() => this.close(), 180));
      list.addEventListener('pointerdown', (e) => e.preventDefault()); // keep focus in the input
      list.addEventListener('click', (e) => {
        const li = e.target.closest('[role="option"]');
        if (li) this.pick(Number(li.dataset.index));
      });
    }

    async run() {
      const q = this.input.value.trim();
      const seq = ++this.seq;
      if (q.length < this.minChars) { this.close(); return; }
      let items = [];
      try { items = (await this.search(q)) || []; } catch (e) { items = []; }
      if (seq !== this.seq) return; // a newer query is running
      this.show(items);
    }

    show(items) {
      this.items = items;
      this.active = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.list.innerHTML = items.length
        ? items.map((it, i) => `<li role="option" class="hof-combo__option" id="${this.list.id}-o${i}" data-index="${i}" aria-selected="false">${this.render(it)}</li>`).join('')
        : `<li class="hof-combo__empty" role="presentation">${esc(this.emptyText)}</li>`;
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    move(delta) {
      const opts = qsa('[role="option"]', this.list);
      if (!opts.length) return;
      this.active = (this.active + delta + opts.length) % opts.length;
      opts.forEach((o, i) => o.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
      const cur = opts[this.active];
      this.input.setAttribute('aria-activedescendant', cur.id);
      cur.scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      const open = !this.list.hidden;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (open) this.move(1); else this.run();
      } else if (e.key === 'ArrowUp') {
        if (open) { e.preventDefault(); this.move(-1); }
      } else if (e.key === 'Enter') {
        e.preventDefault(); // never submit the form from a search box
        if (open && this.active >= 0) this.pick(this.active);
        else if (open && this.items.length === 1) this.pick(0);
      } else if (e.key === 'Escape' || e.key === 'Esc') {
        if (open) { e.preventDefault(); this.close(); }
      }
    }

    pick(i) {
      const item = this.items[i];
      if (!item) return;
      this.close();
      this.onPick(item);
    }
  }

  /* ================================================================
   * Toast
   * ============================================================== */
  function makeToast(portal) {
    const el = document.createElement('div');
    el.className = 'hof-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    portal.appendChild(el);
    let timer;
    return (msg) => {
      el.textContent = msg;
      el.classList.add('is-visible');
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove('is-visible'), 2800);
    };
  }

  /* ================================================================
   * Catch data normalisation
   * ============================================================== */
  function normaliseCatch(c) {
    const photos = (Array.isArray(c.photos) ? c.photos : []).filter((p) => p && p.src);
    const cover = c.cover_photo && c.cover_photo.src ? c.cover_photo : (photos[0] || null);
    const weight = Math.max(0, Math.round(Number(c.weight_oz_total) || 0));
    const venue = c.venue && (c.venue.name || c.venue.handle) ? c.venue : null;
    const gear = (Array.isArray(c.gear) ? c.gear : []).filter((g) => g && g.id != null);
    const species = str(c.species);
    return {
      id: c.id,
      handle: str(c.handle),
      // Public heading from public fields; the metaobject title is the admin list label.
      title: [fmtWeight(weight), species].filter(Boolean).join(' ') + ((venue && venue.name) || str(c.lake_name) ? `, ${(venue && venue.name) || str(c.lake_name)}` : ''),
      angler: str(c.angler_display_name) || 'Anonymous',
      instagram: instagramHandle(c.instagram_handle),
      species,
      weight,
      weightDisplay: fmtWeight(weight) || str(c.weight_display).split(' (')[0],
      kg: weight ? (weight * 0.028349523125).toFixed(2) : '',
      isPb: c.is_personal_best === true,
      fishName: str(c.fish_name),
      venue,
      venueKey: venue ? String(venue.handle || venue.id) : '',
      venueName: venue ? str(venue.name) : '',
      region: venue ? str(venue.region) : '',
      lake: str(c.lake_name),
      date: /^\d{4}-\d{2}-\d{2}$/.test(str(c.catch_date)) ? str(c.catch_date) : '',
      season: str(c.season),
      photos: photos.length ? photos : (cover ? [cover] : []),
      cover,
      story: str(c.story),
      rig: str(c.rig),
      hookSize: str(c.hook_size),
      hookbait: str(c.hookbait_type),
      baitFlavour: str(c.bait_flavour),
      feeding: (Array.isArray(c.feeding_approach) ? c.feeding_approach : []).map(str).filter(Boolean),
      spots: (Array.isArray(c.spot_feature) ? c.spot_feature : []).map(str).filter(Boolean),
      distance: Number(c.distance_m) > 0 ? Math.round(Number(c.distance_m)) : null,
      sessionLength: str(c.session_length),
      featured: c.featured === true,
      gear,
      shareFeed: str(c.share_feed),   // branded share cards (made in the back office), same-origin URLs
      shareStory: str(c.share_story),
      anglerKey: norm(c.angler_display_name),
      sortId: Number(numericId(c.id)) || 0,
    };
  }

  const byNewest = (a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : b.sortId - a.sortId);
  const byHeaviest = (a, b) => (b.weight - a.weight) || byNewest(a, b);

  // Alt text for every catch photo: "[weight] [species] caught by [angler] at [venue or lake]" (as in the Liquid).
  // The file's own alt is not used: it is the internal "Catch photo" marker the back office relies on.
  // Share text: "{weight} {species} on the PB Hall of Fame [trophy emoji] Caught by {angler} at {venue} on PB gear. Think you can beat it?"
  function shareMessage(c) {
    const place = c.venueName || c.lake;
    return `${[fmtWeight(c.weight), c.species].filter(Boolean).join(' ')} on the PB Hall of Fame \u{1F3C6} Caught by ${c.angler}${place ? ` at ${place}` : ''} on PB gear. Think you can beat it?`;
  }
  const SHARE_TAGS = ['PBHallOfFame', 'carpfishing'];
  const shareText = (c, link) => `${shareMessage(c)} ${link} ${SHARE_TAGS.map((t) => `#${t}`).join(' ')}`;
  // WhatsApp goes to api.whatsapp.com directly: the wa.me redirect turns 4-byte UTF-8 (emoji) into "\uFFFD".
  function shareLinks(c, link) {
    return {
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText(c, link))}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`,
      x: `https://x.com/intent/tweet?text=${encodeURIComponent(shareMessage(c))}&url=${encodeURIComponent(link)}&hashtags=${SHARE_TAGS.join(',')}`,
    };
  }

  function catchAlt(c) {
    const place = c.venueName || c.lake;
    return `${[fmtWeight(c.weight), c.species].filter(Boolean).join(' ')} caught by ${c.angler}${place ? ` at ${place}` : ''}`;
  }

  function imgTag(photo, { sizes = '(min-width: 990px) 25vw, 50vw', eager = false, cls = '', alt = '', decorative = false } = {}) {
    if (!photo || !photo.src) return `<span class="hof-noimg ${cls}">${ICON_FISH}</span>`;
    const srcset = [
      photo.small ? `${photo.small} 400w` : '',
      photo.src ? `${photo.src} 800w` : '',
      photo.large ? `${photo.large} 1600w` : '',
    ].filter(Boolean).join(', ');
    const dims = photo.width && photo.height ? ` width="${Number(photo.width)}" height="${Number(photo.height)}"` : '';
    return `<img class="${cls}" src="${esc(photo.src)}" srcset="${esc(srcset)}" sizes="${esc(sizes)}" alt="${decorative ? '' : esc(alt)}"${dims} loading="${eager ? 'eager' : 'lazy'}" decoding="async">`;
  }

  /* ================================================================
   * Hall of Fame page
   * ============================================================== */
  class HallOfFame {
    constructor(root) {
      this.root = root;
      root.hofInstance = this;
      this.uid = uid('hof');
      const cfg = readJSON(root, 'config') || {};
      this.cfg = cfg;
      this.proxy = str(root.dataset.proxy || cfg.proxy || '/apps/hof').replace(/\/{2,}/g, '/').replace(/\/+$/, '');
      this.perPage = Math.min(24, Math.max(6, Number(cfg.per_page) || 12));
      this.moneyFormat = decodeEntities(cfg.money_format || '\u00a3{{amount}}');
      this.siteKey = str(cfg.site_key) || '0x4AAAAAAFBccmuyzdEH9ytW';

      const raw = readJSON(root, 'catches');
      this.catches = (Array.isArray(raw) ? raw : []).filter((c) => c && c.handle).map(normaliseCatch);
      this.byHandle = new Map(this.catches.map((c) => [c.handle, c]));
      const venues = readJSON(root, 'venues');
      this.venues = (Array.isArray(venues) ? venues : []).filter((v) => v && v.name).map((v) => ({
        id: v.id,
        handle: str(v.handle),
        name: str(v.name),
        fishery: str(v.fishery),
        region: str(v.region),
        lat: v.lat == null || v.lat === '' ? null : parseFloat(v.lat),
        lng: v.lng == null || v.lng === '' ? null : parseFloat(v.lng),
        aliases: (Array.isArray(v.aliases) ? v.aliases : []).map(str).filter(Boolean),
      }));
      this.venues.forEach((v) => { v.search = [v.name, v.fishery].concat(v.aliases).map(norm).filter(Boolean); });
      this.customer = readJSON(root, 'customer');

      this.filters = { species: '', venue: '', weight: '', product: '', season: '', sort: 'newest' };
      this.shown = this.perPage;
      this.lbTab = 'all';
      this.lbSpecies = '';
      this.lbBracket = null; // { lo, hi } in lb (hi exclusive) = that club, null = no club filter

      this.cleanups = [];
      this.setupPortal();
      this.setupHero();
      this.setupChart();
      this.setupStickyCta();
      this.renderPage();
      this.bindPage();

      this.detailModal = new Modal(this.detailEl);
      this.detailModal.onRequestClose = () => this.closeDetail();
      this.detailBody = qs('[data-hof-detail-body]', this.detailEl);
      this.bindDetail();

      this.wizard = new SubmitWizard(this, this.submitEl);

      this.onPopState = this.onPopState.bind(this);
      window.addEventListener('popstate', this.onPopState);
      this.openFromUrl(true);
    }

    destroy() {
      (this.cleanups || []).forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
      window.removeEventListener('popstate', this.onPopState);
      if (this.detailModal) this.detailModal.close();
      if (this.wizard) this.wizard.modal.close();
      if (this.portal) this.portal.remove();
      delete this.root.hofInstance;
    }

    // Move popups to <body> so theme stacking contexts can't trap them under the header.
    setupPortal() {
      const portal = document.createElement('div');
      portal.className = 'hof hof-portal';
      portal.setAttribute('data-hof-portal', this.root.dataset.sectionId || '');
      portal.style.cssText = this.root.style.cssText;
      this.detailEl = qs('[data-hof-modal="detail"]', this.root);
      this.submitEl = qs('[data-hof-modal="submit"]', this.root);
      portal.appendChild(this.detailEl);
      portal.appendChild(this.submitEl);
      document.body.appendChild(portal);
      this.portal = portal;
      this.toast = makeToast(portal);
    }

    /* ---------------- page render ---------------- */
    renderPage() {
      const has = this.catches.length > 0;
      if (!has) return;
      this.renderStats();
      this.renderMarquee();
      this.renderFeatured();
      this.renderLeaderboardShell();
      this.renderBrowseShell();
      this.renderGrid();
    }

    renderStats() {
      const wrap = qs('[data-hof-stats]', this.root);
      const biggest = this.catches.reduce((m, c) => (c.weight > m ? c.weight : m), 0);
      const venues = new Set(this.catches.map((c) => c.venueKey).filter(Boolean)).size;
      const anglers = new Set(this.catches.map((c) => c.anglerKey).filter(Boolean)).size;
      const n = this.catches.length;
      const stats = [
        [n, n === 1 ? 'Catch' : 'Catches', 'int'],
        [venues, venues === 1 ? 'Venue' : 'Venues', 'int'],
        [anglers, anglers === 1 ? 'Angler' : 'Anglers', 'int'],
        [biggest, 'Biggest fish', 'weight'],
      ];
      const fmt = (v, kind) => (kind === 'weight' ? (v ? fmtWeight(v) : '-') : Math.round(v).toLocaleString('en-GB'));
      const animate = !reducedMotion() && 'IntersectionObserver' in window;
      wrap.innerHTML = `<dl class="hof-stats__list">${stats.map(([v, l, k]) => `<div class="hof-stat"><dt class="hof-stat__label">${esc(l)}</dt><dd class="hof-stat__value" data-count="${Number(v) || 0}" data-kind="${k}">${esc(fmt(animate ? 0 : v, k))}</dd></div>`).join('')}</dl>`;
      wrap.hidden = false;
      if (!animate) return;
      // Count up once, the first time the strip is properly in view.
      const io = new IntersectionObserver((entries) => {
        if (!entries.some((en) => en.isIntersecting)) return;
        io.disconnect();
        qsa('[data-count]', wrap).forEach((dd) => {
          const target = Number(dd.dataset.count) || 0;
          const kind = dd.dataset.kind;
          const t0 = performance.now();
          const dur = 1400;
          const step = (t) => {
            const p = Math.min(1, (t - t0) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            dd.textContent = fmt(p < 1 ? Math.floor(target * eased) : target, kind);
            if (p < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        });
      }, { threshold: 0.4 });
      io.observe(wrap);
      this.cleanups.push(() => io.disconnect());
    }

    /* ---------------- hero slideshow ---------------- */
    setupHero() {
      const hero = qs('[data-hof-hero]', this.root);
      if (!hero) return;
      this.hero = hero;

      // "See the leaderboard": smooth scroll (instant with reduced motion), keeps the hash usable without JS.
      qsa('[data-hof-scroll-to]', hero).forEach((a) => a.addEventListener('click', (e) => {
        const target = document.getElementById(a.dataset.hofScrollTo);
        if (!target || target.offsetParent === null) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }));

      const clubs = qs('[data-hof-clubs]', hero);
      const topClub = clubs && (qs('.hof-club.is-top', clubs) || qs('.hof-club.is-next', clubs));
      if (clubs) {
        // Fade the right edge only while there is more to swipe to.
        const edge = () => clubs.classList.toggle('is-end', clubs.scrollLeft + clubs.clientWidth >= clubs.scrollWidth - 4);
        clubs.addEventListener('scroll', edge, { passive: true });
        if (topClub && clubs.scrollWidth > clubs.clientWidth) {
          requestAnimationFrame(() => { clubs.scrollLeft = Math.max(0, topClub.offsetLeft - clubs.offsetLeft - 16); edge(); });
        } else edge();
      }

      const video = qs('.hof-hero2__slide--video video', hero);
      if (video && reducedMotion()) { video.removeAttribute('autoplay'); video.pause(); }

      const slides = qsa('[data-hof-slide]', hero);
      if (slides.length < 2) return;
      const dots = qsa('.hof-hero2__dot', hero);
      const record = qs('[data-hof-record]', hero);
      const label = qs('[data-hof-record-label]', hero);
      const weight = qs('[data-hof-record-weight]', hero);
      const meta = qs('[data-hof-record-meta]', hero);
      const link = qs('[data-hof-record-link]', hero);
      const pauseBtn = qs('[data-hof-hero-pause]', hero);

      // Only the first (LCP) image loads with the page; the rest after window load.
      let restLoaded = false;
      const loadRest = () => {
        if (restLoaded) return;
        restLoaded = true;
        slides.slice(1).forEach((sl) => {
          const img = qs('img[data-srcset]', sl);
          if (img) { img.srcset = img.dataset.srcset; img.removeAttribute('data-srcset'); }
        });
      };
      if (document.readyState === 'complete') setTimeout(loadRest, 400);
      else window.addEventListener('load', () => setTimeout(loadRest, 400), { once: true });

      let i = 0;
      let paused = reducedMotion();
      let inView = true;
      let timer = null;
      let swapTimer = null;

      const setRecord = (idx) => {
        const sl = slides[idx];
        label.textContent = idx === 0 ? 'Current record' : `No. ${idx + 1} all time`;
        weight.textContent = sl.dataset.weight || '';
        meta.textContent = sl.dataset.meta || '';
        link.href = `?catch=${encodeURIComponent(sl.dataset.handle || '')}`;
        link.dataset.hofCatch = sl.dataset.handle || '';
      };

      const show = (n) => {
        const prev = slides[i];
        const next = (n + slides.length) % slides.length;
        const cur = slides[next];
        const img = qs('img', cur);
        const go = () => {
          i = next;
          prev.classList.remove('is-active');
          prev.classList.add('is-leaving');
          setTimeout(() => prev.classList.remove('is-leaving'), 1600);
          cur.classList.add('is-active');
          dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
          record.classList.add('is-swapping');
          clearTimeout(swapTimer);
          swapTimer = setTimeout(() => { setRecord(i); record.classList.remove('is-swapping'); }, 380);
        };
        // Never fade to a blank frame: wait for the next photo.
        if (img && (!img.complete || !img.naturalWidth)) {
          loadRest();
          img.addEventListener('load', go, { once: true });
          img.addEventListener('error', () => {}, { once: true });
        } else go();
      };

      const stop = () => { clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        if (paused || !inView || document.hidden) return;
        timer = setInterval(() => show(i + 1), 6000);
      };
      const sync = () => {
        hero.classList.toggle('is-paused', paused || !inView);
        if (pauseBtn) {
          pauseBtn.setAttribute('aria-pressed', String(paused));
          pauseBtn.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
        }
        start();
      };

      if (pauseBtn) pauseBtn.addEventListener('click', () => { paused = !paused; if (!paused) loadRest(); sync(); });
      const onVis = () => sync();
      document.addEventListener('visibilitychange', onVis);
      this.cleanups.push(() => { stop(); document.removeEventListener('visibilitychange', onVis); });
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(([en]) => { inView = en.isIntersecting; sync(); }, { threshold: 0.15 });
        io.observe(hero);
        this.cleanups.push(() => io.disconnect());
      }
      sync();
    }

    /* ---------------- top 5 chart: one-off reveal, No. 5 -> No. 1 ---------------- */
    setupChart() {
      const chart = qs('[data-hof-chart]', this.root);
      if (!chart) return;
      const bars = qsa('[data-hof-bar]', chart); // DOM order: No. 5 ... No. 1
      const real = bars.filter((b) => !b.classList.contains('is-ghost'));
      const ghosts = bars.filter((b) => b.classList.contains('is-ghost'));
      const list = qs('[data-hof-chart-bars]', chart);
      const cap = qs('[data-hof-chart-caption]', chart);
      const capRecord = qs('[data-hof-chart-record]', chart);
      const capTitle = qs('[data-hof-chart-title]', chart);
      const capMeta = qs('[data-hof-chart-meta]', chart);
      const capLink = qs('[data-hof-chart-link]', chart);
      const replay = qs('[data-hof-chart-replay]', chart);
      const armed = chart.classList.contains('is-armed'); // set before first paint unless reduced motion
      const scene = chart.closest('[data-hof-hero]') || chart; // spotlight lives on the whole hero
      const plot = qs('.hof-chart__plot', chart);
      const timers = [];
      const clear = () => { while (timers.length) clearTimeout(timers.pop()); };

      // Spotlight: from the top of the hero onto the featured bar, pool on the chart floor.
      // Computed from the flex layout's end state (not the animating one).
      const aimSpot = () => {
        if (!list) return;
        // Empty wall: no featured bar, so light the No. 1 place (the record that is up for grabs).
        const featured = qs('.hof-bar.is-featured', chart) || bars[bars.length - 1];
        if (!featured) return;
        const grow = qs('.hof-bar.is-featured', chart) ? (parseFloat(getComputedStyle(chart).getPropertyValue('--grow')) || 3) : 1;
        const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
        const n = bars.length;
        const unit = (list.clientWidth - gap * (n - 1)) / (grow + n - 1);
        const k = bars.indexOf(featured);
        const sr = scene.getBoundingClientRect();
        const lr = list.getBoundingClientRect();
        const center = lr.left - sr.left + k * (unit + gap) + (grow * unit) / 2;
        scene.style.setProperty('--spot-x', `${Math.round(center)}px`);
        if (plot) scene.style.setProperty('--floor-y', `${Math.round(plot.getBoundingClientRect().bottom - sr.top)}px`);
      };

      const feature = (bar) => {
        if (!bar || bar.classList.contains('is-ghost')) return;
        bars.forEach((b) => b.classList.toggle('is-featured', b === bar));
        const first = bar.dataset.rank === '1';
        scene.classList.toggle('is-first', first);
        aimSpot();
        if (!cap || !capTitle) return;
        cap.classList.add('is-swap');
        timers.push(setTimeout(() => {
          if (capRecord) capRecord.hidden = !first;
          capTitle.textContent = bar.dataset.title || '';
          if (capMeta) capMeta.textContent = bar.dataset.meta || '';
          if (capLink) {
            capLink.href = `?catch=${encodeURIComponent(bar.dataset.handle || '')}`;
            capLink.dataset.hofCatch = bar.dataset.handle || '';
          }
          cap.classList.remove('is-swap');
        }, armed ? 220 : 0));
      };

      const finish = () => {
        clear();
        bars.forEach((b) => b.classList.add('is-up'));
        chart.classList.remove('is-playing');
        chart.classList.add('is-done');
        if (replay) replay.hidden = !armed || !real.length; // nothing to replay on an empty wall
      };

      const play = () => {
        clear();
        chart.classList.remove('is-done');
        chart.classList.add('is-playing');
        if (replay) replay.hidden = true;
        bars.forEach((b) => b.classList.remove('is-up', 'is-featured'));
        scene.classList.remove('is-first');
        // Open places rise first, together; then the catches one by one, No. 5 up to No. 1.
        timers.push(setTimeout(() => ghosts.forEach((b) => b.classList.add('is-up')), 60));
        real.forEach((b, k) => timers.push(setTimeout(() => { b.classList.add('is-up'); feature(b); }, 150 + k * 2000)));
        const last = real.length ? 150 + (real.length - 1) * 2000 + 900 : 900;
        timers.push(setTimeout(finish, last));
      };

      // Tapping a bar makes it the featured bar (ghost bars open the submit form via the root handler).
      chart.addEventListener('click', (e) => {
        const col = e.target.closest('.hof-bar__col');
        if (!col) return;
        const bar = col.closest('[data-hof-bar]');
        if (!bar || bar.classList.contains('is-ghost')) return;
        e.preventDefault();
        if (chart.classList.contains('is-playing')) finish();
        feature(bar);
      });
      if (replay) replay.addEventListener('click', () => play());
      const onResize = () => aimSpot();
      window.addEventListener('resize', onResize, { passive: true });
      this.cleanups.push(() => { clear(); window.removeEventListener('resize', onResize); });

      if (!armed) { // reduced motion: the end state is already on the page
        feature(qs('.hof-bar.is-featured', chart) || real[real.length - 1]);
        aimSpot();
        return;
      }
      aimSpot();
      // Play once, the first time the chart is properly in view.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          if (!entries.some((en) => en.isIntersecting)) return;
          io.disconnect();
          play();
        }, { threshold: 0.35 });
        io.observe(chart);
        this.cleanups.push(() => io.disconnect());
      } else play();
    }

    /* ---------------- sticky mobile CTA ---------------- */
    setupStickyCta() {
      const bar = qs('[data-hof-sticky-cta]', this.root);
      if (!bar) return;
      if (!this.hero || !('IntersectionObserver' in window)) { bar.remove(); return; }
      // Lives in the body-level portal so theme transforms can't break position: fixed.
      this.portal.appendChild(bar);
      bar.hidden = false;
      bar.addEventListener('click', (e) => {
        const b = e.target.closest('[data-hof-open-submit]');
        if (b) { e.preventDefault(); this.openSubmit(b); }
      });
      const io = new IntersectionObserver(([en]) => {
        bar.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0);
      }, { threshold: 0 });
      io.observe(this.hero);
      this.cleanups.push(() => io.disconnect());
    }

    /* ---------------- recent catches marquee ---------------- */
    renderMarquee() {
      const wrap = qs('[data-hof-marquee]', this.root);
      if (!wrap) return;
      const recent = this.catches.filter((c) => c.cover && c.cover.src).sort(byNewest).slice(0, 16);
      if (recent.length < 4) { wrap.hidden = true; return; }
      const item = (c, copy) => `
        <a class="hof-marquee__item" href="?catch=${encodeURIComponent(c.handle)}" data-hof-catch="${esc(c.handle)}"${copy ? ' tabindex="-1"' : ''}>
          <img src="${esc(c.cover.small || c.cover.src)}" alt="${copy ? '' : esc(catchAlt(c))}" loading="lazy" decoding="async" width="160" height="120">
          <span class="hof-marquee__text"><b>${esc(fmtWeight(c.weight))}</b> ${esc(c.species)}</span>
        </a>`;
      wrap.innerHTML = `
        <div class="hof-marquee__track" style="--hof-marquee-duration: ${Math.max(40, recent.length * 6)}s">
          <div class="hof-marquee__group">${recent.map((c) => item(c, false)).join('')}</div>
          <div class="hof-marquee__group" aria-hidden="true">${recent.map((c) => item(c, true)).join('')}</div>
        </div>`;
      wrap.hidden = false;
    }

    cardHTML(c, { featuredLabel = '', sizes } = {}) {
      const place = [c.venueName, c.lake].filter(Boolean).join(', ');
      return `
        <a class="hof-card${featuredLabel ? ' hof-card--featured' : ''}" href="?catch=${encodeURIComponent(c.handle)}" data-hof-catch="${esc(c.handle)}">
          <span class="hof-card__media">
            ${imgTag(c.cover, { sizes: sizes || '(min-width: 1200px) 300px, (min-width: 750px) 33vw, 50vw', alt: catchAlt(c) })}
            <span class="hof-card__badges">
              ${featuredLabel ? `<span class="hof-badge hof-badge--dark">${esc(featuredLabel)}</span>` : ''}
              ${c.isPb ? '<span class="hof-badge">New PB</span>' : ''}
            </span>
          </span>
          <span class="hof-card__body">
            <span class="hof-card__weight">${esc(fmtWeight(c.weight))}</span>
            <span class="hof-card__species">${esc(c.species)}${c.fishName ? ` <span class="hof-card__fishname">"${esc(c.fishName)}"</span>` : ''}</span>
            <span class="hof-card__angler">${esc(c.angler)}</span>
            <span class="hof-card__meta">${esc([place, fmtDate(c.date)].filter(Boolean).join(' \u00b7 '))}</span>
          </span>
        </a>`;
    }

    renderFeatured() {
      const list = this.catches.filter((c) => c.featured).sort(byNewest).slice(0, 4);
      const wrap = qs('[data-hof-featured-wrap]', this.root);
      if (!list.length) { wrap.hidden = true; return; }
      const [top, ...rest] = list;
      qs('[data-hof-featured]', this.root).innerHTML = `
        <div class="hof-featured${rest.length ? '' : ' hof-featured--single'}">
          <div class="hof-featured__main">${this.cardHTML(top, { featuredLabel: 'Catch of the Month', sizes: '(min-width: 990px) 50vw, 100vw' })}</div>
          ${rest.length ? `<div class="hof-featured__rest">${rest.map((c) => this.cardHTML(c, { featuredLabel: 'Featured', sizes: '(min-width: 990px) 25vw, 50vw' })).join('')}</div>` : ''}
        </div>`;
      wrap.hidden = false;
    }

    /* ---------------- leaderboards ---------------- */
    renderLeaderboardShell() {
      const wrap = qs('[data-hof-lb-wrap]', this.root);
      const species = SPECIES_ORDER.filter((s) => this.catches.some((c) => c.species === s));
      this.catches.forEach((c) => { if (c.species && species.indexOf(c.species) === -1) species.push(c.species); });
      this.lbSpeciesList = species;
      this.lbSpecies = species[0] || '';
      const tabs = [['all', 'All time'], ['season', 'This season'], ['month', 'This month'], ['species', 'By species']];
      const id = this.uid;
      qs('[data-hof-lb]', this.root).innerHTML = `
        <div class="hof-tabs" role="tablist" aria-label="Leaderboards">
          ${tabs.map(([k, label]) => `<button type="button" class="hof-tab" role="tab" id="${id}-tab-${k}" aria-controls="${id}-lbpanel" aria-selected="${k === this.lbTab}" tabindex="${k === this.lbTab ? 0 : -1}" data-hof-tab="${k}">${esc(label)}</button>`).join('')}
        </div>
        <div class="hof-lb__panel" role="tabpanel" id="${id}-lbpanel" aria-labelledby="${id}-tab-${this.lbTab}" tabindex="0">
          <div class="hof-lb__bar">
            <p class="hof-lb__sub" data-hof-lb-sub></p>
            <button type="button" class="hof-lb__club" data-hof-lb-club hidden aria-label="Show all weights"></button>
            <label class="hof-select hof-select--inline" data-hof-lb-species-wrap hidden>
              <span class="hof-select__label">Species</span>
              <select class="hof-input" data-hof-lb-species>${species.map((s) => `<option value="${esc(s)}">${esc(s)}</option>`).join('')}</select>
            </label>
          </div>
          <ol class="hof-lb__list" data-hof-lb-list></ol>
        </div>`;
      wrap.hidden = false;
      this.updateLeaderboard();
    }

    leaderboardRows() {
      const today = londonISO();
      let list = this.catches;
      let sub = 'The heaviest fish in the Hall of Fame.';
      if (this.lbTab === 'season') {
        const s = seasonFor(today);
        list = list.filter((c) => c.date && c.date >= s.start && c.date <= today);
        sub = `${s.label}: the heaviest fish so far.`;
      } else if (this.lbTab === 'month') {
        const ym = today.slice(0, 7);
        list = list.filter((c) => c.date && c.date.slice(0, 7) === ym);
        const m = Number(today.slice(5, 7));
        sub = `${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][m - 1]} ${today.slice(0, 4)}: the heaviest fish so far.`;
      } else if (this.lbTab === 'species') {
        list = list.filter((c) => c.species === this.lbSpecies);
        sub = this.lbSpecies ? `Top 10 by weight: ${this.lbSpecies}.` : '';
      }
      if (this.lbBracket) {
        const { lo, hi } = this.lbBracket;
        list = list.filter((c) => c.weight >= lo * 16 && c.weight < hi * 16);
        sub = `${lo}lb+ club: ${list.length} fish from ${lo}lb to ${hi - 1}lb 15oz.`;
      }
      return { rows: list.slice().sort(byHeaviest).slice(0, 10), sub };
    }

    updateLeaderboard() {
      const { rows, sub } = this.leaderboardRows();
      qs('[data-hof-lb-sub]', this.root).textContent = sub;
      qs('[data-hof-lb-species-wrap]', this.root).hidden = this.lbTab !== 'species';
      const clubChip = qs('[data-hof-lb-club]', this.root);
      clubChip.hidden = !this.lbBracket;
      if (this.lbBracket) clubChip.innerHTML = `${esc(this.lbBracket.lo)}lb+ club <span aria-hidden="true">\u00d7</span>`;
      const panel = qs(`#${this.uid}-lbpanel`, this.root);
      panel.setAttribute('aria-labelledby', `${this.uid}-tab-${this.lbTab}`);
      const listEl = qs('[data-hof-lb-list]', this.root);
      if (!rows.length) {
        listEl.innerHTML = `<li class="hof-lb__empty">No catches here yet. Could yours be the first? <button type="button" class="hof-link" data-hof-open-submit aria-haspopup="dialog">Submit your catch</button></li>`;
        return;
      }
      listEl.innerHTML = rows.map((c, i) => `
        <li class="hof-lb__row${i < 3 ? ' hof-lb__row--top' : ''}">
          <a class="hof-lb__link" href="?catch=${encodeURIComponent(c.handle)}" data-hof-catch="${esc(c.handle)}">
            <span class="hof-lb__rank" aria-label="Rank ${i + 1}">${i + 1}</span>
            <span class="hof-lb__thumb">${imgTag(c.cover, { sizes: '64px', alt: catchAlt(c) })}</span>
            <span class="hof-lb__main">
              <span class="hof-lb__weight">${esc(fmtWeight(c.weight))}</span>
              <span class="hof-lb__meta">${esc([c.species, c.angler].filter(Boolean).join(' \u00b7 '))}${c.isPb ? ' <span class="hof-badge hof-badge--sm">PB</span>' : ''}</span>
            </span>
            <span class="hof-lb__venue">${esc(c.venueName || c.lake || '')}</span>
          </a>
        </li>`).join('');
    }

    showClub(lo, hi) {
      this.lbBracket = Number.isFinite(lo) && Number.isFinite(hi) && hi > lo ? { lo, hi } : null;
      this.selectTab('all', false);
      this.updateLeaderboard();
      const target = qs(`#${this.uid}-lbpanel`, this.root);
      const heading = document.getElementById(`${this.root.id}-lb`) || target;
      if (heading) {
        heading.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
        if (target) target.focus({ preventScroll: true });
      }
    }

    selectTab(key, focus) {
      this.lbTab = key;
      qsa('[data-hof-tab]', this.root).forEach((t) => {
        const on = t.dataset.hofTab === key;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      this.updateLeaderboard();
    }

    /* ---------------- grid + filters ---------------- */
    renderBrowseShell() {
      const id = this.uid;
      const species = SPECIES_ORDER.filter((s) => this.catches.some((c) => c.species === s));
      const venueMap = new Map();
      let hasUnlisted = false;
      this.catches.forEach((c) => {
        if (c.venueKey) venueMap.set(c.venueKey, c.venueName);
        else hasUnlisted = true;
      });
      const venues = Array.from(venueMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
      const productCounts = new Map();
      this.catches.forEach((c) => c.gear.forEach((g) => {
        const k = String(g.id);
        const cur = productCounts.get(k) || { title: g.title, n: 0 };
        cur.n += 1;
        productCounts.set(k, cur);
      }));
      const products = Array.from(productCounts.entries()).sort((a, b) => b[1].n - a[1].n || String(a[1].title).localeCompare(String(b[1].title)));
      const seasonLatest = new Map();
      this.catches.forEach((c) => {
        if (!c.season) return;
        if (!seasonLatest.has(c.season) || c.date > seasonLatest.get(c.season)) seasonLatest.set(c.season, c.date);
      });
      const seasons = Array.from(seasonLatest.entries()).sort((a, b) => (b[1] > a[1] ? 1 : -1)).map((e) => e[0]);

      const select = (key, label, options) => `
        <label class="hof-select">
          <span class="hof-select__label">${esc(label)}</span>
          <select class="hof-input" data-hof-filter="${key}">${options.map(([v, l]) => `<option value="${esc(v)}">${esc(l)}</option>`).join('')}</select>
        </label>`;

      qs('[data-hof-browse]', this.root).innerHTML = `
        <div class="hof-toolbar">
          <button type="button" class="hof-btn hof-btn--ghost hof-btn--sm hof-toolbar__toggle" aria-expanded="false" aria-controls="${id}-filters" data-hof-filters-toggle>
            Filters <span class="hof-toolbar__count" data-hof-filter-count></span>
          </button>
          ${select('sort', 'Sort', [['newest', 'Newest'], ['heaviest', 'Heaviest']]).replace('class="hof-select"', 'class="hof-select hof-select--sort"')}
          <div class="hof-filters" id="${id}-filters" data-hof-filters>
            ${select('species', 'Species', [['', 'All species']].concat(species.map((s) => [s, s])))}
            ${select('venue', 'Venue', [['', 'All venues']].concat(venues, hasUnlisted ? [['__other', 'Other venues']] : []))}
            ${select('weight', 'Weight', WEIGHT_BUCKETS)}
            ${products.length ? select('product', 'Gear', [['', 'Any gear']].concat(products.map(([k, v]) => [k, v.title]))) : ''}
            ${seasons.length ? select('season', 'Season', [['', 'All seasons']].concat(seasons.map((s) => [s, s]))) : ''}
            <button type="button" class="hof-link hof-filters__reset" data-hof-reset hidden>Clear filters</button>
          </div>
        </div>
        <p class="hof-results" data-hof-results aria-live="polite"></p>
        <div class="hof-grid" data-hof-grid></div>
        <div class="hof-more-wrap"><button type="button" class="hof-btn hof-btn--ghost" data-hof-load-more hidden>Load more</button></div>`;
      qs('[data-hof-browse-wrap]', this.root).hidden = false;
    }

    filtered() {
      const f = this.filters;
      let min = 0;
      let max = Infinity;
      if (f.weight) {
        const [a, b] = f.weight.split('-');
        min = a ? Number(a) * 16 : 0;
        max = b ? Number(b) * 16 : Infinity;
      }
      const list = this.catches.filter((c) =>
        (!f.species || c.species === f.species) &&
        (!f.venue || (f.venue === '__other' ? !c.venueKey : c.venueKey === f.venue)) &&
        (!f.product || c.gear.some((g) => String(g.id) === f.product)) &&
        (!f.season || c.season === f.season) &&
        c.weight >= min && c.weight < max);
      return list.sort(f.sort === 'heaviest' ? byHeaviest : byNewest);
    }

    renderGrid(focusIndex) {
      const list = this.filtered();
      const grid = qs('[data-hof-grid]', this.root);
      const visible = list.slice(0, this.shown);
      if (!list.length) {
        grid.innerHTML = `<div class="hof-grid__empty"><p>No catches match these filters.</p><button type="button" class="hof-btn hof-btn--ghost hof-btn--sm" data-hof-reset>Clear filters</button></div>`;
      } else {
        grid.innerHTML = visible.map((c) => this.cardHTML(c)).join('');
      }
      const active = ['species', 'venue', 'weight', 'product', 'season'].filter((k) => this.filters[k]).length;
      qs('[data-hof-filter-count]', this.root).textContent = active ? `(${active})` : '';
      qsa('.hof-filters [data-hof-reset]', this.root).forEach((b) => { b.hidden = !active; });
      qs('[data-hof-results]', this.root).textContent = list.length
        ? `Showing ${visible.length} of ${list.length} ${list.length === 1 ? 'catch' : 'catches'}`
        : '';
      const more = qs('[data-hof-load-more]', this.root);
      more.hidden = visible.length >= list.length;
      if (typeof focusIndex === 'number') {
        const cards = qsa('[data-hof-catch]', grid);
        if (cards[focusIndex]) cards[focusIndex].focus();
      }
    }

    resetFilters() {
      Object.assign(this.filters, { species: '', venue: '', weight: '', product: '', season: '' });
      qsa('[data-hof-filter]', this.root).forEach((s) => { if (s.dataset.hofFilter !== 'sort') s.value = ''; });
      this.shown = this.perPage;
      this.renderGrid();
    }

    /* ---------------- events ---------------- */
    bindPage() {
      this.root.addEventListener('click', (e) => {
        const open = e.target.closest('[data-hof-open-submit]');
        if (open) { e.preventDefault(); this.openSubmit(open); return; }

        const club = e.target.closest('[data-hof-bracket]');
        if (club) { e.preventDefault(); this.showClub(Number(club.dataset.hofBracket), Number(club.dataset.hofBracketTo)); return; }
        if (e.target.closest('[data-hof-lb-club]')) { this.lbBracket = null; this.updateLeaderboard(); return; }

        const link = e.target.closest('[data-hof-catch]');
        if (link) {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) return; // let "open in new tab" work
          e.preventDefault();
          this.openDetail(link.dataset.hofCatch, { opener: link });
          return;
        }

        const tab = e.target.closest('[data-hof-tab]');
        if (tab) { this.selectTab(tab.dataset.hofTab, false); return; }

        if (e.target.closest('[data-hof-reset]')) { this.resetFilters(); return; }

        if (e.target.closest('[data-hof-load-more]')) {
          const prev = this.shown;
          this.shown += this.perPage;
          this.renderGrid(prev);
          return;
        }

        const toggle = e.target.closest('[data-hof-filters-toggle]');
        if (toggle) {
          const open2 = toggle.getAttribute('aria-expanded') !== 'true';
          toggle.setAttribute('aria-expanded', open2 ? 'true' : 'false');
          qs('[data-hof-filters]', this.root).classList.toggle('is-open', open2);
        }
      });

      this.root.addEventListener('change', (e) => {
        const f = e.target.closest('[data-hof-filter]');
        if (f) {
          this.filters[f.dataset.hofFilter] = f.value;
          this.shown = this.perPage;
          this.renderGrid();
          return;
        }
        if (e.target.matches('[data-hof-lb-species]')) {
          this.lbSpecies = e.target.value;
          this.updateLeaderboard();
        }
      });

      this.root.addEventListener('keydown', (e) => {
        const tab = e.target.closest('[data-hof-tab]');
        if (!tab) return;
        const tabs = qsa('[data-hof-tab]', this.root);
        const i = tabs.indexOf(tab);
        let next = -1;
        if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next > -1) {
          e.preventDefault();
          this.selectTab(tabs[next].dataset.hofTab, true);
        }
      });
    }

    openSubmit(opener) {
      if (this.detailModal.isOpen) this.closeDetail();
      this.wizard.open(opener);
    }

    /* ---------------- deep links / history ---------------- */
    urlWith(handle) {
      const u = new URL(window.location.href);
      if (handle) u.searchParams.set('catch', handle);
      else u.searchParams.delete('catch');
      return u.pathname + u.search + u.hash;
    }

    openFromUrl(initial) {
      const handle = new URLSearchParams(window.location.search).get('catch');
      if (handle) {
        if (this.byHandle.has(handle)) {
          this.openDetail(handle, { push: false });
        } else if (initial) {
          history.replaceState(history.state, '', this.urlWith(''));
          this.toast("That catch isn't in the Hall of Fame (yet).");
        }
      } else if (initial && /^#submit$/i.test(window.location.hash)) {
        this.openSubmit();
      }
    }

    onPopState() {
      const handle = new URLSearchParams(window.location.search).get('catch');
      if (handle && this.byHandle.has(handle)) {
        this.openDetail(handle, { push: false });
      } else if (this.detailModal.isOpen) {
        this.detailPushed = false;
        this.detailModal.close();
      }
    }

    /* ---------------- catch detail popup ---------------- */
    openDetail(handle, { push = true, opener } = {}) {
      const c = this.byHandle.get(handle);
      if (!c) return false;
      const titleId = uid('hof-dt');
      this.detailBody.innerHTML = this.detailHTML(c, titleId);
      this.detailModal.panel.removeAttribute('aria-label');
      this.detailModal.panel.setAttribute('aria-labelledby', titleId);
      this.detailBody.scrollTop = 0;
      this.currentCatch = c;
      this.prefetchShareFile(c);
      const wasOpen = this.detailModal.isOpen;
      if (!wasOpen) this.detailModal.open(opener);
      this.bindGallery(this.detailBody);
      this.detailModal.focus();
      if (push) {
        const state = Object.assign({}, history.state || {}, { hofCatch: handle });
        if (wasOpen && this.detailPushed) history.replaceState(state, '', this.urlWith(handle));
        else history.pushState(state, '', this.urlWith(handle));
        this.detailPushed = true;
      } else if (!wasOpen) {
        this.detailPushed = false;
      }
      return true;
    }

    closeDetail() {
      if (!this.detailModal.isOpen) return;
      if (this.detailPushed && history.state && history.state.hofCatch) {
        history.back(); // popstate closes the popup
        // Safety net in case popstate doesn't fire (e.g. history was replaced by another script).
        setTimeout(() => {
          if (this.detailModal.isOpen && !new URLSearchParams(window.location.search).get('catch')) {
            this.detailPushed = false;
            this.detailModal.close();
          }
        }, 400);
      } else {
        const state = Object.assign({}, history.state || {});
        delete state.hofCatch;
        history.replaceState(state, '', this.urlWith(''));
        this.detailPushed = false;
        this.detailModal.close();
      }
    }

    detailHTML(c, titleId) {
      const photos = c.photos;
      const gallery = photos.length ? `
        <div class="hof-gallery" role="region" aria-roledescription="carousel" aria-label="Photos">
          <div class="hof-gallery__track" data-hof-track tabindex="0" aria-label="Photos, use arrow keys to browse">
            ${photos.map((p, i) => `<figure class="hof-gallery__slide" role="group" aria-roledescription="slide" aria-label="${i + 1} of ${photos.length}">${imgTag(p, { sizes: '(min-width: 990px) 600px, 100vw', eager: i === 0, alt: catchAlt(c) })}</figure>`).join('')}
          </div>
          ${photos.length > 1 ? `
            <button type="button" class="hof-gallery__nav hof-gallery__nav--prev" data-hof-prev aria-label="Previous photo">${ICON_PREV}</button>
            <button type="button" class="hof-gallery__nav hof-gallery__nav--next" data-hof-next-photo aria-label="Next photo">${ICON_NEXT}</button>
            <div class="hof-gallery__dots">${photos.map((_, i) => `<button type="button" class="hof-gallery__dot" data-hof-dot="${i}" aria-label="Show photo ${i + 1}"></button>`).join('')}</div>` : ''}
        </div>` : `<div class="hof-gallery hof-gallery--empty">${ICON_FISH}</div>`;

      const facts = [
        ['Angler', `${esc(c.angler)}${c.instagram && /^[A-Za-z0-9._]{1,30}$/.test(c.instagram) ? ` <a class="hof-ig" href="https://www.instagram.com/${encodeURIComponent(c.instagram)}/" target="_blank" rel="noopener nofollow">@${esc(c.instagram)}</a>` : ''}`],
        ['Species', esc(c.species)],
        ['Fish name', esc(c.fishName)],
        ['Venue', esc([c.venueName, c.region].filter(Boolean).join(', '))],
        ['Lake', esc(c.lake)],
        ['Date', esc(fmtDate(c.date))],
      ].filter(([, v]) => v);

      const tactics = [
        ['Rig', c.rig],
        ['Hook size', c.hookSize],
        ['Hookbait', c.hookbait],
        ['Bait flavour', c.baitFlavour],
        ['Feeding', c.feeding.join(', ')],
        ['Spot', c.spots.join(', ')],
        ['Distance', c.distance ? `${c.distance} m` : ''],
        ['Session', c.sessionLength],
      ].filter(([, v]) => v);

      const gear = c.gear.map((g) => this.gearHTML(g)).join('');

      return `
        <article class="hof-detail">
          <div class="hof-detail__media">${gallery}</div>
          <div class="hof-detail__info">
            <div class="hof-detail__badges">
              ${c.featured ? '<span class="hof-badge hof-badge--dark">Featured</span>' : ''}
              ${c.isPb ? '<span class="hof-badge">New PB</span>' : ''}
            </div>
            <p class="hof-detail__weight">${esc(c.weightDisplay)}${c.kg ? `<small class="hof-detail__kg">${esc(c.kg)}kg</small>` : ''}</p>
            <h2 class="hof-detail__title" id="${titleId}">${esc(c.title)}</h2>
            <dl class="hof-facts">${facts.map(([k, v]) => `<div class="hof-facts__row"><dt>${esc(k)}</dt><dd>${v}</dd></div>`).join('')}</dl>
            <div class="hof-share">
              <button type="button" class="hof-btn hof-btn--ghost hof-btn--sm" data-hof-share aria-expanded="false" aria-controls="${titleId}-share">${ICON_SHARE}<span>Share this catch</span></button>
              ${this.shareMenuHTML(c, `${titleId}-share`)}
            </div>
            ${c.story ? `<div class="hof-detail__section"><h3 class="hof-h3">The story</h3><p class="hof-story">${esc(c.story)}</p></div>` : ''}
            ${tactics.length ? `<div class="hof-detail__section"><h3 class="hof-h3">Tactics</h3><dl class="hof-facts hof-facts--tactics">${tactics.map(([k, v]) => `<div class="hof-facts__row"><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl></div>` : ''}
            ${gear ? `<div class="hof-detail__section"><h3 class="hof-h3">Gear used</h3><ul class="hof-gear-list">${gear}</ul></div>` : ''}
          </div>
        </article>`;
    }

    gearHTML(g) {
      const variants = Array.isArray(g.variants) ? g.variants : [];
      const ids = variants.map((v) => String(v.id));
      // Preselect the variant the angler used; fall back to the first available one
      // when it's sold out, so the card still offers something to buy.
      const firstAvail = variants.find((v) => v.available) || variants[0];
      let selected = String(g.variant_id || '');
      if (g.preferred_variant_id != null && ids.indexOf(String(g.preferred_variant_id)) > -1) {
        const pref = variants.find((v) => String(v.id) === String(g.preferred_variant_id));
        selected = pref.available || !firstAvail || !firstAvail.available ? String(pref.id) : String(firstAvail.id);
      } else if (g.preferred_variant_id != null && !ids.length) {
        selected = String(g.preferred_variant_id);
      }
      if (ids.length && ids.indexOf(selected) === -1) selected = String(firstAvail.id);
      const current = variants.find((v) => String(v.id) === selected);
      const available = current ? current.available : g.available;
      const price = current ? decodeEntities(current.price_formatted) : decodeEntities(g.price_formatted);
      const compare = !current && g.compare_at_price_formatted ? decodeEntities(g.compare_at_price_formatted) : '';
      const selId = uid('hof-var');
      return `
        <li class="hof-gear" data-hof-gear="${esc(g.id)}">
          <a class="hof-gear__media" href="${esc(g.url || '#')}" tabindex="-1" aria-hidden="true">
            ${g.image ? `<img src="${esc(g.image)}" alt="" loading="lazy" decoding="async" width="150" height="150">` : `<span class="hof-noimg">${ICON_FISH}</span>`}
          </a>
          <div class="hof-gear__info">
            <a class="hof-gear__title" href="${esc(g.url || '#')}">${esc(g.title)}</a>
            <p class="hof-gear__price"><span data-hof-gear-price>${esc(price)}</span>${compare ? ` <s class="hof-gear__compare">${esc(compare)}</s>` : ''}</p>
            ${variants.length > 1 ? `
              <label class="hof-sr-only" for="${selId}">Option for ${esc(g.title)}</label>
              <select class="hof-input hof-input--sm" id="${selId}" data-hof-gear-variant>
                ${variants.map((v) => `<option value="${esc(v.id)}"${String(v.id) === selected ? ' selected' : ''}${v.available ? '' : ' data-soldout="1"'}>${esc(v.title)}${v.available ? '' : ' (sold out)'}</option>`).join('')}
              </select>` : ''}
            <button type="button" class="hof-btn hof-btn--sm hof-gear__add" data-hof-add data-variant-id="${esc(selected)}"${available ? '' : ' disabled'}>${available ? 'Add to cart' : 'Sold out'}</button>
          </div>
        </li>`;
    }

    bindDetail() {
      this.detailBody.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-hof-gear-variant]');
        if (!sel) return;
        const li = sel.closest('[data-hof-gear]');
        const g = this.currentCatch && this.currentCatch.gear.find((x) => String(x.id) === li.dataset.hofGear);
        const v = g && (g.variants || []).find((x) => String(x.id) === sel.value);
        const btn = qs('[data-hof-add]', li);
        btn.dataset.variantId = sel.value;
        if (v) {
          qs('[data-hof-gear-price]', li).textContent = decodeEntities(v.price_formatted);
          btn.disabled = !v.available;
          btn.textContent = v.available ? 'Add to cart' : 'Sold out';
        }
      });

      this.detailBody.addEventListener('click', async (e) => {
        const add = e.target.closest('[data-hof-add]');
        if (add) {
          if (add.disabled || add.getAttribute('aria-busy') === 'true') return;
          const label = add.textContent;
          add.setAttribute('aria-busy', 'true');
          add.textContent = 'Adding...';
          try {
            await addToCart(add.dataset.variantId, 1, (drawer) => this.detailModal.yieldTo(drawer));
            add.textContent = 'Added';
            this.toast('Added to your cart');
            setTimeout(() => { add.textContent = label; }, 2000);
          } catch (err) {
            add.textContent = label;
            this.toast(err.message || 'Could not add to cart');
          } finally {
            add.removeAttribute('aria-busy');
          }
          return;
        }
        const shareBtn = e.target.closest('[data-hof-share]');
        if (shareBtn) { this.share(this.currentCatch, shareBtn); return; }
        if (e.target.closest('[data-hof-copy-link]')) this.copyLink(this.currentCatch);
      });
    }

    bindGallery(scope) {
      const track = qs('[data-hof-track]', scope);
      if (!track) return;
      const slides = qsa('.hof-gallery__slide', track);
      const dots = qsa('[data-hof-dot]', scope);
      const prev = qs('[data-hof-prev]', scope);
      const next = qs('[data-hof-next-photo]', scope);
      const index = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
      const go = (i) => {
        const to = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: to * track.clientWidth, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      const update = () => {
        const i = index();
        dots.forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= slides.length - 1;
      };
      track.addEventListener('scroll', rafThrottle(update), { passive: true });
      if (prev) prev.addEventListener('click', () => go(index() - 1));
      if (next) next.addEventListener('click', () => go(index() + 1));
      dots.forEach((d) => d.addEventListener('click', () => go(Number(d.dataset.hofDot))));
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(index() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(index() + 1); }
      });
      update();
    }

    /* ---------------- sharing ---------------- */
    // Share links go through the app proxy (/apps/hof/c/{handle}): a small page with the catch's own link preview
    // (branded card) that sends people on to this popup.
    shareLink(c) {
      return `${window.location.origin}${this.proxy}/c/${encodeURIComponent(c.handle)}`;
    }

    // Phones get the native share sheet (with the story card); desktops get this menu.
    shareMenuHTML(c, id) {
      const link = this.shareLink(c);
      const image = c.shareFeed || (c.cover && (c.cover.large || c.cover.src)) || '';
      const to = shareLinks(c, link);
      return `
        <div class="hof-share__menu" id="${id}" role="group" aria-label="Share this catch" hidden>
          <button type="button" class="hof-share__item" data-hof-copy-link>Copy link</button>
          <a class="hof-share__item" href="${esc(to.whatsapp)}" target="_blank" rel="noopener">WhatsApp</a>
          <a class="hof-share__item" href="${esc(to.facebook)}" target="_blank" rel="noopener">Facebook</a>
          <a class="hof-share__item" href="${esc(to.x)}" target="_blank" rel="noopener">X</a>
          ${image ? `<a class="hof-share__item" href="${esc(image)}" download="pb-hall-of-fame-${esc(c.handle)}.jpg">Download image</a>` : ''}
        </div>`;
    }

    nativeShare() {
      return !!navigator.share && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    }

    // Fetch the story card as soon as the popup opens, so the share sheet opens within the tap
    // (browsers drop the tap's permission while a download is still running).
    prefetchShareFile(c) {
      this.shareFile = null;
      if (!c.shareStory || !this.nativeShare() || !navigator.canShare) return;
      const handle = c.handle;
      fetch(c.shareStory, { credentials: 'omit' })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => {
          if (!blob || !this.currentCatch || this.currentCatch.handle !== handle) return;
          const file = new File([blob], `pb-hall-of-fame-${handle}.jpg`, { type: blob.type || 'image/jpeg' });
          if (navigator.canShare({ files: [file] })) this.shareFile = { handle, file };
        })
        .catch(() => {});
    }

    async share(c, btn) {
      if (!c) return;
      const link = this.shareLink(c);
      if (this.nativeShare()) {
        const file = this.shareFile && this.shareFile.handle === c.handle ? this.shareFile.file : null;
        try {
          if (file) await navigator.share({ files: [file], text: shareText(c, link) });
          else await navigator.share({ title: `${fmtWeight(c.weight)} ${c.species} \u00b7 PB Hall of Fame`, text: `${shareMessage(c)} ${SHARE_TAGS.map((t) => `#${t}`).join(' ')}`, url: link });
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') return;
          // Share sheet not available after all: fall back to the menu.
        }
      }
      const menu = btn && document.getElementById(btn.getAttribute('aria-controls'));
      if (!menu) return;
      const open = menu.hidden;
      menu.hidden = !open;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) { const first = qs('.hof-share__item', menu); if (first) first.focus(); }
    }

    async copyLink(c) {
      const url = this.shareLink(c);
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
          copied = true;
        }
      } catch (e) { copied = false; }
      if (!copied) {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        this.detailEl.appendChild(ta);
        ta.select();
        try { copied = document.execCommand('copy'); } catch (e) { copied = false; }
        ta.remove();
      }
      this.toast(copied ? 'Link copied' : url);
    }
  }

  /* ================================================================
   * Submit popup (5-step wizard)
   * ============================================================== */
  class SubmitWizard {
    constructor(hof, el) {
      this.hof = hof;
      this.el = el;
      this.modal = new Modal(el);
      this.modal.onRequestClose = () => this.close();
      this.form = qs('[data-hof-form]', el);
      this.body = qs('[data-hof-form-body]', el);
      this.steps = qsa('.hof-step', el);
      this.step = 1;
      this.photos = [];
      this.picked = [];
      this.queue = Promise.resolve();
      this.tsToken = '';
      this.tsWidget = null;
      this.initialised = false;
      this.submitting = false;
      this.done = false;
      this.saveDraftSoon = debounce(() => this.saveDraft(), 300);
      this.bind();
    }

    field(name) { return qs(`[name="${name}"]`, this.form); }
    val(name) { const f = this.field(name); return f ? str(f.value) : ''; }
    checked(name) { const f = this.field(name); return !!(f && f.checked); }
    listVal(name) { return qsa(`input[name="${name}"]:checked`, this.form).map((i) => i.value); }

    /* ---------------- open / close / reset ---------------- */
    open(opener) {
      if (this.done) this.resetAll();
      if (!this.initialised) {
        this.initialised = true;
        const restored = this.restoreDraft();
        qs('[data-hof-draft-notice]', this.el).hidden = !restored;
        this.prefillCustomer();
        this.syncAll();
      }
      qs('[name="catch_date"]', this.form).max = latestToday();
      this.modal.open(opener);
      this.showStep(this.step, { focus: true });
    }

    close() {
      this.modal.close();
      if (this.done) this.resetAll();
    }

    prefillCustomer() {
      const c = this.hof.customer;
      if (!c) return;
      [['first_name', c.first_name], ['last_name', c.last_name], ['email', c.email]].forEach(([k, v]) => {
        const f = this.field(k);
        if (f && !f.value && v) f.value = v;
      });
    }

    resetAll() {
      this.photos.forEach((p) => {
        if (p.xhr) { try { p.xhr.abort(); } catch (e) { /* ignore */ } }
        if (p.preview) URL.revokeObjectURL(p.preview);
      });
      this.photos = [];
      this.picked = [];
      this.form.reset();
      qsa('input[type="hidden"]', this.form).forEach((i) => { i.value = ''; });
      qsa('[data-error-for]', this.form).forEach((b) => this.clearError(b.dataset.errorFor));
      this.formError('');
      this.tsToken = '';
      if (this.tsWidget != null && window.turnstile) { try { window.turnstile.reset(this.tsWidget); } catch (e) { /* ignore */ } }
      this.done = false;
      this.submitting = false;
      this.step = 1;
      qs('[data-hof-success]', this.el).hidden = true;
      qs('[data-hof-form-footer]', this.el).hidden = false;
      qs('[data-hof-progress]', this.el).hidden = false;
      qs('[data-hof-draft-notice]', this.el).hidden = true;
      qs('[data-hof-exif-note]', this.el).hidden = true;
      const more = qs('[data-hof-more]', this.el);
      more.hidden = true;
      qs('[data-hof-more-toggle]', this.el).setAttribute('aria-expanded', 'false');
      qs('[data-hof-product-input]', this.el).value = '';
      qs('[data-hof-venue-input]', this.el).value = '';
      this.prefillCustomer();
      this.syncAll();
      this.showStep(1);
    }

    syncAll() {
      this.renderPhotos();
      this.syncVenueUI();
      this.renderPicked();
      this.updateStoryCount();
      const moreHasData = ['distance_m', 'fish_in_session', 'other_gear', 'story'].some((n) => this.val(n)) || this.listVal('spot_feature').length > 0;
      if (moreHasData) this.toggleMore(true);
    }

    /* ---------------- events ---------------- */
    bind() {
      const f = this.form;

      f.addEventListener('submit', (e) => {
        e.preventDefault();
        if (this.done || this.submitting) return;
        if (this.step < 5) this.next();
        else this.submit();
      });

      qs('[data-hof-back]', this.el).addEventListener('click', () => {
        if (this.step > 1) this.showStep(this.step - 1, { focus: true });
      });

      f.addEventListener('input', (e) => {
        const name = e.target.name;
        if (name) {
          this.clearError(ERROR_ALIASES[name] || name);
          if (name === 'weight_oz') this.clearError('weight_lb');
          if (name === 'venue_free_text') this.clearError('venue_id');
        }
        if (name === 'story') this.updateStoryCount();
        if (e.target.hasAttribute('data-draft')) this.saveDraftSoon();
      });

      f.addEventListener('change', (e) => {
        const t = e.target;
        if (t.name) this.clearError(ERROR_ALIASES[t.name] || t.name);
        if (t.name === 'venue_not_listed') {
          if (t.checked) { this.field('venue_id').value = ''; this.field('venue_label').value = ''; }
          this.clearError('venue_id');
          this.syncVenueUI();
          if (t.checked) { const free = this.field('venue_free_text'); if (free) free.focus(); }
        }
        if (t.name === 'instagram') t.value = instagramHandle(t.value) ? `@${instagramHandle(t.value)}` : '';
        if (t.name === 'home_area') t.value = t.value.toUpperCase().trim();
        if (t.hasAttribute('data-draft')) this.saveDraftSoon();
      });

      qs('[data-hof-clear-draft]', this.el).addEventListener('click', () => {
        local.remove(DRAFT_KEY);
        this.resetAll();
        this.showStep(1, { focus: true });
      });

      qs('[data-hof-more-toggle]', this.el).addEventListener('click', () => {
        const open = qs('[data-hof-more-toggle]', this.el).getAttribute('aria-expanded') !== 'true';
        this.toggleMore(open);
      });

      // Photos
      const input = qs('[data-hof-photo-input]', this.el);
      input.addEventListener('change', () => {
        if (input.files && input.files.length) this.addFiles(input.files);
        input.value = '';
      });
      qs('[data-hof-photos]', this.el).addEventListener('click', (e) => {
        const rm = e.target.closest('[data-hof-photo-remove]');
        if (rm) { this.removePhoto(rm.dataset.hofPhotoRemove); return; }
        const retry = e.target.closest('[data-hof-photo-retry]');
        if (retry) this.retryPhoto(retry.dataset.hofPhotoRetry);
      });

      // Venue combobox
      this.venueCombo = new Combo({
        input: qs('[data-hof-venue-input]', this.el),
        list: qs('[data-hof-venue-list]', this.el),
        search: (q) => this.searchVenues(q),
        render: (v) => (v.notListed
          ? `<span class="hof-combo__text"><span class="hof-combo__title">My venue isn't listed</span><span class="hof-combo__sub">Type it in yourself</span></span>`
          : `<span class="hof-combo__text"><span class="hof-combo__title">${esc(v.name)}</span><span class="hof-combo__sub">${esc([v.fishery !== v.name ? v.fishery : '', v.region].filter(Boolean).join(' \u00b7 '))}</span></span>`),
        onPick: (v) => {
          if (v.notListed) {
            const box = this.field('venue_not_listed');
            box.checked = true;
            this.field('venue_id').value = '';
            this.field('venue_label').value = '';
            this.syncVenueUI();
            this.saveDraftSoon();
            const free = this.field('venue_free_text');
            if (free) { if (!free.value) free.value = qs('[data-hof-venue-input]', this.el).value.trim(); free.focus(); }
          } else {
            this.selectVenue(v);
            const lake = this.field('lake_name');
            if (lake) lake.focus();
          }
        },
      });
      qs('[data-hof-venue-clear]', this.el).addEventListener('click', () => {
        this.field('venue_id').value = '';
        this.field('venue_label').value = '';
        this.syncVenueUI();
        this.saveDraftSoon();
        const vi = qs('[data-hof-venue-input]', this.el);
        vi.value = '';
        vi.focus();
      });

      // Product combobox (Shopify predictive search)
      this.productCombo = new Combo({
        input: qs('[data-hof-product-input]', this.el),
        list: qs('[data-hof-product-list]', this.el),
        minChars: 2,
        delay: 250,
        emptyText: 'No products found',
        search: (q) => this.searchProducts(q),
        render: (p) => `
          <span class="hof-combo__thumb">${p.image ? `<img src="${esc(sizedImg(p.image, 96))}" alt="" width="40" height="40" loading="lazy">` : ''}</span>
          <span class="hof-combo__text"><span class="hof-combo__title">${esc(p.title)}</span>${p.priceText ? `<span class="hof-combo__sub">${esc(p.priceText)}</span>` : ''}</span>
          ${this.isPicked(p.id) ? '<span class="hof-combo__tick" aria-label="Added">&#10003;</span>' : ''}`,
        onPick: (p) => {
          this.pickProduct(p);
          const pi = qs('[data-hof-product-input]', this.el);
          pi.value = '';
          pi.focus();
        },
      });

      const picked = qs('[data-hof-picked]', this.el);
      picked.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-hof-picked-remove]');
        if (rm) this.unpickProduct(rm.dataset.hofPickedRemove);
      });
      picked.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-hof-picked-variant]');
        if (!sel) return;
        const entry = this.picked.find((p) => p.id === sel.dataset.hofPickedVariant);
        if (entry) { entry.variant_id = sel.value; this.saveDraftSoon(); }
      });
    }

    toggleMore(open) {
      qs('[data-hof-more-toggle]', this.el).setAttribute('aria-expanded', open ? 'true' : 'false');
      qs('[data-hof-more]', this.el).hidden = !open;
    }

    updateStoryCount() {
      const n = this.val('story').length;
      const el = qs('[data-hof-story-count]', this.el);
      el.textContent = `${n} / ${MAX_STORY}`;
      el.classList.toggle('is-over', n > MAX_STORY);
    }

    /* ---------------- steps ---------------- */
    showStep(n, { focus = false } = {}) {
      this.step = n;
      this.steps.forEach((s) => { s.hidden = this.done || Number(s.dataset.step) !== n; });
      qs('[data-hof-progress-label]', this.el).textContent = `Step ${n} of 5: ${STEP_TITLES[n - 1]}`;
      qs('[data-hof-progress-fill]', this.el).style.width = `${(n / 5) * 100}%`;
      const bar = qs('[data-hof-progress-bar]', this.el);
      bar.setAttribute('aria-valuenow', String(n));
      bar.setAttribute('aria-valuetext', `Step ${n} of 5, ${STEP_TITLES[n - 1]}`);
      qs('[data-hof-back]', this.el).hidden = n === 1;
      const nextBtn = qs('[data-hof-next]', this.el);
      if (!this.submitting) nextBtn.textContent = n === 5 ? 'Submit catch' : 'Next';
      this.formError('');
      this.body.scrollTop = 0;
      if (n === 5) this.renderTurnstile();
      if (focus) {
        const h = qs(`.hof-step[data-step="${n}"] .hof-step__title`, this.el);
        if (h) this.modal.focus(h);
      }
    }

    next() {
      if (!this.validateStep(this.step)) { this.focusFirstError(); return; }
      this.showStep(this.step + 1, { focus: true });
    }

    /* ---------------- validation ---------------- */
    errorBox(name) { return qs(`[data-error-for="${name}"]`, this.form); }

    inputFor(name) {
      if (name === 'photos') return qs('[data-hof-photo-input]', this.el);
      if (name === 'venue_id') {
        return this.checked('venue_not_listed') ? this.field('venue_free_text') : qs('[data-hof-venue-input]', this.el);
      }
      if (name === 'products') return qs('[data-hof-product-input]', this.el);
      if (name === 'turnstile_token') return qs('[data-hof-turnstile]', this.el);
      return this.field(name);
    }

    setError(name, msg) {
      const box = this.errorBox(name);
      if (!box) { this.formError(msg); return; }
      box.textContent = msg;
      box.hidden = false;
      if (!box.id) box.id = uid('hof-err');
      const input = this.inputFor(name);
      if (input && input.matches('input, select, textarea')) {
        input.setAttribute('aria-invalid', 'true');
        const ids = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
        if (ids.indexOf(box.id) === -1) ids.push(box.id);
        input.setAttribute('aria-describedby', ids.join(' '));
      }
      if (name === 'weight_lb') { const oz = this.field('weight_oz'); if (oz) oz.setAttribute('aria-invalid', 'true'); }
    }

    clearError(name) {
      const box = this.errorBox(name);
      if (box) { box.hidden = true; box.textContent = ''; }
      const input = this.inputFor(name);
      if (input && input.removeAttribute) input.removeAttribute('aria-invalid');
      if (name === 'weight_lb') { const oz = this.field('weight_oz'); if (oz) oz.removeAttribute('aria-invalid'); }
      if (name === 'venue_id') { const vi = qs('[data-hof-venue-input]', this.el); vi.removeAttribute('aria-invalid'); }
    }

    formError(msg) {
      const el = qs('[data-hof-form-error]', this.el);
      el.textContent = msg || '';
      el.hidden = !msg;
    }

    focusFirstError() {
      const step = qs(`.hof-step[data-step="${this.step}"]`, this.el);
      const box = step && qsa('[data-error-for]', step).find((b) => !b.hidden);
      if (!box) return;
      const target = this.inputFor(box.dataset.errorFor);
      if (target && target.focus && target.getClientRects().length) {
        this.modal.focus(target);
        target.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      } else {
        box.scrollIntoView({ block: 'center' });
      }
    }

    validateStep(n) {
      const errors = [];
      const add = (name, msg) => errors.push([name, msg]);
      const intIn = (name, min, max) => {
        const s = this.val(name);
        if (s === '') return true;
        const x = Number(s);
        return Number.isInteger(x) && x >= min && x <= max;
      };

      if (n === 1) {
        const good = this.photos.filter((p) => p.status !== 'error');
        if (!this.photos.length) add('photos', 'Add at least one photo of your fish.');
        else if (!good.length) add('photos', 'Your photos could not be uploaded. Try again or choose different photos.');
        else if (this.photos.some((p) => p.status === 'error')) add('photos', 'One photo could not be added. Retry or remove it to carry on.');
      }

      if (n === 2) {
        if (!this.val('species')) add('species', 'Choose the species.');
        const lbS = this.val('weight_lb');
        const ozS = this.val('weight_oz');
        const lb = lbS === '' ? 0 : Number(lbS);
        const oz = ozS === '' ? 0 : Number(ozS);
        if (!Number.isInteger(lb) || lb < 0 || lb > 150) add('weight_lb', 'Pounds must be a whole number from 0 to 150.');
        else if (!Number.isInteger(oz) || oz < 0 || oz > 15) add('weight_lb', 'Ounces must be a whole number from 0 to 15.');
        else if (lb * 16 + oz <= 0) add('weight_lb', 'Enter the weight of your fish.');
      }

      if (n === 3) {
        if (this.checked('venue_not_listed')) {
          if (this.val('venue_free_text').length < 2) add('venue_free_text', 'Type the name of your venue.');
        } else if (!this.val('venue_id')) {
          add('venue_id', "Pick your venue from the list, or tick \"My venue isn't listed\".");
        }
        const d = this.val('catch_date');
        if (!d) add('catch_date', 'Enter the date you caught it.');
        else if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || Number.isNaN(Date.parse(d))) add('catch_date', 'Enter a valid date.');
        else if (d > latestToday()) add('catch_date', "The date can't be in the future.");
        else if (d < '1950-01-01') add('catch_date', 'Enter a valid date.');
        const t = this.val('catch_time');
        if (t && !/^([01]\d|2[0-3]):[0-5]\d/.test(t)) add('catch_time', 'Enter a valid time, or leave it empty.');
      }

      if (n === 4) {
        if (!intIn('distance_m', 0, 500)) add('distance_m', 'Distance must be a whole number from 0 to 500.');
        if (!intIn('fish_in_session', 0, 200)) add('fish_in_session', 'Enter a whole number.');
        if (this.val('story').length > MAX_STORY) add('story', `Keep your story under ${MAX_STORY} characters.`);
      }

      if (n === 5) {
        if (!this.val('first_name')) add('first_name', 'Enter your first name.');
        if (!this.val('last_name')) add('last_name', 'Enter your last name.');
        const email = this.val('email');
        if (!email) add('email', 'Enter your email address.');
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) add('email', 'Enter a valid email address.');
        const ig = instagramHandle(this.val('instagram'));
        if (ig && !/^[A-Za-z0-9._]{1,30}$/.test(ig)) add('instagram', 'Instagram handles use letters, numbers, dots and underscores.');
        const area = this.val('home_area').toUpperCase().replace(/\s+/g, '');
        if (area && !/^[A-Z]{1,2}\d[A-Z\d]?(\d[A-Z]{2})?$/.test(area)) add('home_area', 'Enter the first part of your postcode, like SG12.');
        if (!this.checked('consent_website')) add('consent_website', 'Please confirm we can show your photo in the Hall of Fame.');
        if (!this.checked('consent_age')) add('consent_age', 'Please confirm your age.');
        if (!this.tsToken) {
          add('turnstile_token', this.tsFailed
            ? 'The security check could not load. Check your connection or turn off content blockers, then try again.'
            : 'Please wait for the security check to finish.');
          if (this.tsFailed) this.renderTurnstile(true);
        }
      }

      const step = qs(`.hof-step[data-step="${n}"]`, this.el);
      qsa('[data-error-for]', step).forEach((b) => this.clearError(b.dataset.errorFor));
      errors.forEach(([name, msg]) => this.setError(name, msg));
      return errors.length === 0;
    }

    /* ---------------- photos ---------------- */
    addFiles(fileList) {
      const files = Array.from(fileList);
      const slots = MAX_PHOTOS - this.photos.length;
      this.clearError('photos');
      if (slots <= 0) { this.setError('photos', 'You can add up to 3 photos.'); return; }
      if (files.length > slots) this.setError('photos', `You can add up to 3 photos, so we added the first ${slots}.`);
      files.slice(0, slots).forEach((file) => {
        const p = { id: uid('hof-ph'), file, status: 'processing', progress: 0, preview: '', resourceUrl: '', error: '', exif: null };
        this.photos.push(p);
        this.enqueue(p);
      });
      this.renderPhotos();
    }

    // One photo at a time keeps memory low on phones.
    enqueue(p) {
      this.queue = this.queue.then(() => this.processPhoto(p)).catch(() => {});
    }

    async processPhoto(p) {
      if (this.photos.indexOf(p) === -1) return;
      const file = p.file;
      try {
        if (file.type && !/^image\//.test(file.type) && !isHeic(file)) {
          throw Object.assign(new Error("That file isn't a photo. Please choose a JPEG or PNG."), { friendly: true });
        }
        if (!p.blob) {
          p.status = 'processing';
          this.renderPhotos();
          try {
            const head = await readBlobBuffer(file.slice(0, 256 * 1024));
            p.exif = readExif(head);
          } catch (e) { p.exif = null; }
          if (p.exif) this.applyExif(p.exif);
          try {
            p.blob = await reencodeImage(file);
          } catch (err) {
            if (isHeic(file)) {
              throw Object.assign(new Error("This photo is in HEIC format, which this browser can't open. Please choose a JPEG, or on iPhone set Settings > Camera > Formats to Most Compatible."), { friendly: true });
            }
            throw Object.assign(new Error("We couldn't read this photo. Please try a different one (JPEG or PNG)."), { friendly: true });
          }
          if (this.photos.indexOf(p) === -1) return;
          p.preview = URL.createObjectURL(p.blob);
        }
        p.status = 'uploading';
        p.progress = 0;
        p.error = '';
        this.renderPhotos();
        await this.uploadPhoto(p);
        if (this.photos.indexOf(p) === -1) return;
        p.status = 'done';
        p.progress = 100;
        this.clearError('photos');
        this.renderPhotos();
      } catch (err) {
        if (err && err.aborted) return;
        if (this.photos.indexOf(p) === -1) return;
        p.status = 'error';
        p.error = (err && err.friendly) ? err.message : (err && err.message) || 'Upload failed. Please try again.';
        this.renderPhotos();
      }
    }

    async uploadPhoto(p) {
      const index = this.photos.indexOf(p) + 1;
      const filename = `catch-photo-${Date.now()}-${index}.jpg`;
      let res;
      try {
        res = await fetch(`${this.hof.proxy}/upload-url`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ files: [{ filename, mimeType: 'image/jpeg', fileSize: p.blob.size }] }),
        });
      } catch (e) {
        throw new Error('Upload could not start. Check your connection and try again.');
      }
      let data = null;
      try { data = await res.json(); } catch (e) { data = null; }
      const target = data && data.ok && Array.isArray(data.targets) ? data.targets[0] : null;
      if (!res.ok || !target || !target.url) {
        throw new Error((data && data.error) || (res.status === 429 ? 'Too many uploads. Please wait a few minutes and try again.' : 'Upload could not start. Please try again.'));
      }
      await uploadToTarget(target, p.blob, filename, (pct) => {
        p.progress = pct;
        this.updatePhotoProgress(p);
      }, (xhr) => { p.xhr = xhr; });
      p.xhr = null;
      p.resourceUrl = target.resourceUrl;
    }

    removePhoto(id) {
      const i = this.photos.findIndex((p) => p.id === id);
      if (i === -1) return;
      const [p] = this.photos.splice(i, 1);
      if (p.xhr) { try { p.xhr.abort(); } catch (e) { /* ignore */ } }
      if (p.preview) URL.revokeObjectURL(p.preview);
      this.clearError('photos');
      this.renderPhotos();
      const add = qs('[data-hof-photo-input]', this.el);
      if (add) add.focus();
    }

    retryPhoto(id) {
      const p = this.photos.find((x) => x.id === id);
      if (!p || p.status !== 'error') return;
      p.status = p.blob ? 'uploading' : 'processing';
      p.error = '';
      this.clearError('photos');
      this.renderPhotos();
      this.enqueue(p);
    }

    renderPhotos() {
      const list = qs('[data-hof-photos]', this.el);
      list.innerHTML = this.photos.map((p, i) => {
        const n = i + 1;
        let status = '';
        if (p.status === 'processing') status = '<span class="hof-photo__text">Preparing...</span>';
        else if (p.status === 'uploading') status = `<span class="hof-photo__bar"><span data-hof-photo-bar style="width:${p.progress}%"></span></span><span class="hof-photo__text" data-hof-photo-pct>Uploading ${p.progress}%</span>`;
        else if (p.status === 'done') status = '<span class="hof-photo__text hof-photo__text--ok">Uploaded</span>';
        else if (p.status === 'error') status = `<span class="hof-photo__text hof-photo__text--err">${esc(p.error)}</span>${p.blob ? `<button type="button" class="hof-link" data-hof-photo-retry="${p.id}">Retry</button>` : ''}`;
        return `
          <li class="hof-photo is-${p.status}" data-photo-id="${p.id}">
            <span class="hof-photo__thumb">${p.preview ? `<img src="${p.preview}" alt="Photo ${n}">` : (p.status === 'error' ? `<span class="hof-noimg">${ICON_FISH}</span>` : '<span class="hof-spinner" aria-hidden="true"></span>')}</span>
            <span class="hof-photo__status">${status}</span>
            <button type="button" class="hof-photo__remove" data-hof-photo-remove="${p.id}" aria-label="Remove photo ${n}">${ICON_CLOSE}</button>
          </li>`;
      }).join('');
      qs('[data-hof-photo-add]', this.el).hidden = this.photos.length >= MAX_PHOTOS;
    }

    updatePhotoProgress(p) {
      const li = qs(`[data-photo-id="${p.id}"]`, this.el);
      if (!li) return;
      const bar = qs('[data-hof-photo-bar]', li);
      const pct = qs('[data-hof-photo-pct]', li);
      if (bar) bar.style.width = `${p.progress}%`;
      if (pct) pct.textContent = `Uploading ${p.progress}%`;
    }

    applyExif(exif) {
      const notes = [];
      if (exif.date) {
        const dateField = this.field('catch_date');
        if (dateField && !dateField.value && exif.date <= latestToday() && exif.date >= '1950-01-01') {
          dateField.value = exif.date;
          notes.push('date');
          const timeField = this.field('catch_time');
          if (timeField && !timeField.value && exif.time) timeField.value = exif.time;
        }
      }
      if (exif.gps && !this.val('venue_id') && !this.checked('venue_not_listed')) {
        let best = null;
        this.hof.venues.forEach((v) => {
          if (v.lat == null || v.lng == null || Number.isNaN(v.lat) || Number.isNaN(v.lng)) return;
          const d = haversineKm(exif.gps.lat, exif.gps.lng, v.lat, v.lng);
          if (d <= VENUE_RADIUS_KM && (!best || d < best.d)) best = { v, d };
        });
        if (best) { this.selectVenue(best.v); notes.push('venue'); }
      }
      if (notes.length) {
        const el = qs('[data-hof-exif-note]', this.el);
        el.textContent = `We filled in the ${notes.join(' and ')} from your photo. You can check ${notes.length > 1 ? 'them' : 'it'} in step 3.`;
        el.hidden = false;
        this.saveDraftSoon();
      }
    }

    /* ---------------- venues ---------------- */
    searchVenues(q) {
      const n = norm(q);
      if (!n) return [];
      const words = n.split(' ');
      const scored = [];
      this.hof.venues.forEach((v) => {
        let score = 0;
        v.search.forEach((h, i) => {
          let s = 0;
          if (h === n) s = 5;
          else if (h.indexOf(n) === 0) s = 4;
          else if (` ${h}`.indexOf(` ${n}`) > -1) s = 3;
          else if (h.indexOf(n) > -1) s = 2;
          else if (words.length > 1 && words.every((w) => h.indexOf(w) > -1)) s = 1;
          if (i === 0 && s) s += 0.5; // prefer name matches
          if (s > score) score = s;
        });
        if (score) scored.push({ v, score });
      });
      scored.sort((a, b) => b.score - a.score || a.v.name.localeCompare(b.v.name));
      return scored.slice(0, 8).map((s) => s.v).concat([{ notListed: true }]);
    }

    selectVenue(v) {
      this.field('venue_id').value = String(v.id);
      this.field('venue_label').value = v.fishery && v.fishery !== v.name ? `${v.name}, ${v.fishery}` : v.name;
      this.field('venue_not_listed').checked = false;
      qs('[data-hof-venue-input]', this.el).value = '';
      this.clearError('venue_id');
      this.syncVenueUI();
      this.saveDraftSoon();
    }

    syncVenueUI() {
      const id = this.val('venue_id');
      const notListed = this.checked('venue_not_listed');
      const picked = !!id && !notListed;
      qs('[data-hof-venue-picked]', this.el).hidden = !picked;
      qs('[data-hof-venue-picked-name]', this.el).textContent = picked ? (this.val('venue_label') || 'Venue selected') : '';
      qs('[data-hof-venue-search]', this.el).hidden = picked || notListed;
      qs('[data-hof-venue-free]', this.el).hidden = !notListed;
    }

    /* ---------------- products ---------------- */
    fromSuggest(p) {
      const image = p.image || (p.featured_image && p.featured_image.url) || null;
      let priceText = '';
      if (p.price != null && p.price !== '') {
        const s = String(p.price);
        const cents = s.indexOf('.') > -1 ? Math.round(parseFloat(s) * 100) : Number(s);
        if (Number.isFinite(cents)) priceText = formatMoney(cents, this.hof.moneyFormat);
      }
      return { id: String(p.id), handle: p.handle, title: p.title, image, priceText };
    }

    async searchProducts(q, abortable = true) {
      let ctrl = null;
      if (abortable) {
        if (this.searchAbort) this.searchAbort.abort();
        ctrl = window.AbortController ? new AbortController() : null;
        this.searchAbort = ctrl;
      }
      const url = `${shopRoot()}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
      const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined });
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      return products.filter((p) => p && p.id && p.handle).map((p) => this.fromSuggest(p));
    }

    isPicked(id) { return this.picked.some((p) => p.id === String(id)); }

    pickProduct(item) {
      const id = String(item.id);
      if (this.isPicked(id)) return;
      if (this.picked.length >= MAX_PRODUCTS) { this.setError('products', `You can add up to ${MAX_PRODUCTS} products.`); return; }
      const entry = { id, handle: item.handle, title: item.title, image: item.image || null, variants: null, variant_id: '' };
      this.picked.push(entry);
      this.clearError('products');
      this.renderPicked();
      this.saveDraftSoon();
      this.loadVariants(entry);
    }

    unpickProduct(id) {
      this.picked = this.picked.filter((p) => p.id !== String(id));
      this.clearError('products');
      this.renderPicked();
      this.saveDraftSoon();
    }

    async loadVariants(entry) {
      try {
        const res = await fetch(`${shopRoot()}products/${encodeURIComponent(entry.handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) { entry.variants = []; return; }
        const p = await res.json();
        if (!entry.image && p.featured_image) entry.image = p.featured_image;
        const variants = Array.isArray(p.variants) ? p.variants : [];
        entry.variants = variants.length > 1 ? variants.map((v) => ({ id: String(v.id), title: v.title })) : [];
        if (entry.variant_id && !entry.variants.some((v) => v.id === entry.variant_id)) entry.variant_id = '';
        if (this.picked.indexOf(entry) > -1) { this.renderPicked(); this.saveDraftSoon(); }
      } catch (e) {
        entry.variants = [];
      }
    }

    renderPicked() {
      const list = qs('[data-hof-picked]', this.el);
      list.innerHTML = this.picked.map((p) => {
        const selId = uid('hof-pv');
        return `
          <li class="hof-picked__item">
            <span class="hof-picked__thumb">${p.image ? `<img src="${esc(sizedImg(p.image, 96))}" alt="" width="44" height="44" loading="lazy">` : ''}</span>
            <span class="hof-picked__info">
              <span class="hof-picked__title">${esc(p.title)}</span>
              ${p.variants && p.variants.length ? `
                <label class="hof-sr-only" for="${selId}">Size or option for ${esc(p.title)}</label>
                <select class="hof-input hof-input--sm" id="${selId}" data-hof-picked-variant="${esc(p.id)}">
                  <option value="">Size / option (optional)</option>
                  ${p.variants.map((v) => `<option value="${esc(v.id)}"${v.id === p.variant_id ? ' selected' : ''}>${esc(v.title)}</option>`).join('')}
                </select>` : ''}
            </span>
            <button type="button" class="hof-picked__remove" data-hof-picked-remove="${esc(p.id)}" aria-label="Remove ${esc(p.title)}">${ICON_CLOSE}</button>
          </li>`;
      }).join('');
    }

    /* ---------------- Turnstile ---------------- */
    loadTurnstile() {
      if (window.turnstile) return Promise.resolve(window.turnstile);
      if (this.tsPromise) return this.tsPromise;
      this.tsPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-hof-turnstile-script]');
        const s = existing || document.createElement('script');
        const done = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')));
        s.addEventListener('load', done);
        s.addEventListener('error', () => reject(new Error('turnstile')));
        if (!existing) {
          s.src = TURNSTILE_SRC;
          s.async = true;
          s.setAttribute('data-hof-turnstile-script', '');
          document.head.appendChild(s);
        }
      }).catch((err) => { this.tsPromise = null; throw err; });
      return this.tsPromise;
    }

    /** Sends a Turnstile error code with the storefront hostname to the Worker logs (once per code per page). */
    reportTurnstileError(code) {
      const host = location.hostname;
      console.warn('[Hall of Fame] Turnstile error', code, host);
      this.tsReported = this.tsReported || new Set();
      if (!code || this.tsReported.has(code)) return;
      this.tsReported.add(code);
      try {
        fetch(`${this.hof.proxy}/client-log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ evt: 'turnstile_error', code, host, path: location.pathname }),
          keepalive: true,
          credentials: 'same-origin',
        }).catch(() => {});
      } catch (e) { /* logging must never break the form */ }
    }

    async renderTurnstile(force) {
      if (this.tsWidget != null && !force) return;
      if (this.tsRendering) return;
      this.tsRendering = true;
      const box = qs('[data-hof-turnstile]', this.el);
      try {
        const ts = await this.loadTurnstile();
        if (this.tsWidget != null) { try { ts.remove(this.tsWidget); } catch (e) { /* ignore */ } this.tsWidget = null; }
        this.tsFailed = false;
        this.tsWidget = ts.render(box, {
          sitekey: box.dataset.sitekey || this.hof.siteKey,
          theme: 'dark',
          size: 'flexible',
          action: 'hof_submit',
          callback: (token) => { this.tsToken = token; this.clearError('turnstile_token'); },
          'expired-callback': () => { this.tsToken = ''; },
          'timeout-callback': () => { this.tsToken = ''; },
          'error-callback': (code) => {
            this.tsToken = '';
            const c = String(code || '');
            this.reportTurnstileError(c);
            // 110xxx = widget configuration (e.g. 110200: this domain isn't allowed for the site key). Retrying won't help.
            if (/^110/.test(c)) {
              this.setError('turnstile_token', `Something went wrong on our side (code ${c}). Please try again later.`);
              return true; // handled: stop Turnstile's own retries/console noise
            }
            this.setError('turnstile_token', 'The security check hit a problem. It will retry, or reload the page.');
            return false;
          },
        });
      } catch (e) {
        this.tsFailed = true;
        this.setError('turnstile_token', 'The security check could not load. Check your connection or turn off content blockers, then try again.');
      } finally {
        this.tsRendering = false;
      }
    }

    resetTurnstile() {
      this.tsToken = '';
      if (this.tsWidget != null && window.turnstile) {
        try { window.turnstile.reset(this.tsWidget); } catch (e) { /* ignore */ }
      }
    }

    /* ---------------- draft (localStorage, no photos, no consents) ---------------- */
    saveDraft() {
      if (this.done) return;
      const fields = {};
      qsa('[data-draft]', this.form).forEach((el) => {
        if (!el.name) return;
        if (el.dataset.draft === 'list') {
          if (!fields[el.name]) fields[el.name] = [];
          if (el.checked) fields[el.name].push(el.value);
        } else if (el.type === 'checkbox') {
          fields[el.name] = el.checked;
        } else {
          fields[el.name] = el.value;
        }
      });
      const products = this.picked.map((p) => ({ id: p.id, handle: p.handle, title: p.title, image: p.image, variants: p.variants, variant_id: p.variant_id }));
      local.set(DRAFT_KEY, JSON.stringify({ v: 1, saved: Date.now(), fields, products }));
    }

    restoreDraft() {
      let d = null;
      try { d = JSON.parse(local.get(DRAFT_KEY) || 'null'); } catch (e) { d = null; }
      if (!d || d.v !== 1 || typeof d.fields !== 'object' || !d.fields) return false;
      if (d.saved && Date.now() - d.saved > DRAFT_MAX_AGE_MS) { local.remove(DRAFT_KEY); return false; }
      let meaningful = false;
      qsa('[data-draft]', this.form).forEach((el) => {
        if (!el.name || !(el.name in d.fields)) return;
        const v = d.fields[el.name];
        if (el.dataset.draft === 'list') {
          el.checked = Array.isArray(v) && v.indexOf(el.value) > -1;
          if (el.checked) meaningful = true;
        } else if (el.type === 'checkbox') {
          el.checked = v === true;
        } else if (typeof v === 'string') {
          el.value = v;
          if (v && el.type !== 'hidden') meaningful = true;
        }
      });
      if (this.val('venue_id') && !this.hof.venues.some((v) => String(v.id) === this.val('venue_id'))) {
        this.field('venue_id').value = ''; // venue no longer exists
        this.field('venue_label').value = '';
      }
      this.picked = (Array.isArray(d.products) ? d.products : [])
        .filter((p) => p && p.id && p.handle)
        .slice(0, MAX_PRODUCTS)
        .map((p) => ({ id: String(p.id), handle: String(p.handle), title: String(p.title || p.handle), image: p.image || null, variants: Array.isArray(p.variants) ? p.variants : null, variant_id: p.variant_id ? String(p.variant_id) : '' }));
      this.picked.forEach((p) => { if (!p.variants) this.loadVariants(p); });
      if (this.picked.length) meaningful = true;
      return meaningful;
    }

    /* ---------------- submit ---------------- */
    buildPayload() {
      const num = (name) => {
        const s = this.val(name);
        if (s === '') return null;
        const x = Number(s);
        return Number.isFinite(x) ? Math.round(x) : null;
      };
      const notListed = this.checked('venue_not_listed');
      const area = this.val('home_area').toUpperCase().replace(/\s+/g, '');
      const outward = (/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})?$/.exec(area) || [])[1] || area;
      const exifPhoto = this.photos.find((p) => p.status === 'done' && p.exif && p.exif.iso);
      return {
        photos: this.photos.filter((p) => p.status === 'done' && p.resourceUrl).map((p) => p.resourceUrl),
        species: this.val('species'),
        weight_lb: num('weight_lb') || 0,
        weight_oz: num('weight_oz') || 0,
        is_pb: this.checked('is_pb'),
        fish_name: this.val('fish_name'),
        venue_id: notListed ? '' : toGid(this.val('venue_id'), 'Metaobject'),
        venue_free_text: notListed ? this.val('venue_free_text') : '',
        lake_name: this.val('lake_name'),
        catch_date: this.val('catch_date'),
        catch_time: (this.val('catch_time').match(/^\d{2}:\d{2}/) || [''])[0],
        session_length: this.val('session_length'),
        swim_peg: this.val('swim_peg'),
        products: this.picked.map((p) => toGid(p.id, 'Product')),
        variants: this.picked.filter((p) => p.variant_id).map((p) => toGid(p.variant_id, 'ProductVariant')),
        rig: this.val('rig'),
        hook_size: this.val('hook_size'),
        hookbait_type: this.val('hookbait_type'),
        bait_brand: this.val('bait_brand'),
        bait_flavour: this.val('bait_flavour'),
        feeding_approach: this.listVal('feeding_approach'),
        spot_feature: this.listVal('spot_feature'),
        distance_m: num('distance_m'),
        fish_in_session: num('fish_in_session'),
        other_gear: this.val('other_gear'),
        story: this.val('story').slice(0, MAX_STORY),
        first_name: this.val('first_name'),
        last_name: this.val('last_name'),
        email: this.val('email'),
        instagram: instagramHandle(this.val('instagram')),
        home_area: outward,
        experience_level: this.val('experience_level'),
        consent_website: this.checked('consent_website'),
        consent_ads: this.checked('consent_ads'),
        consent_marketing: this.checked('consent_marketing'),
        consent_age: this.checked('consent_age'),
        turnstile_token: this.tsToken || '',
        website: (this.field('website') || {}).value || '',
        exif_date: exifPhoto ? exifPhoto.exif.iso : '',
        source: getSource(),
      };
    }

    setBusy(on, label) {
      this.submitting = on;
      const next = qs('[data-hof-next]', this.el);
      const back = qs('[data-hof-back]', this.el);
      next.disabled = on;
      back.disabled = on;
      next.setAttribute('aria-busy', on ? 'true' : 'false');
      next.textContent = on ? (label || 'Submitting...') : (this.step === 5 ? 'Submit catch' : 'Next');
    }

    async submit() {
      if (this.submitting) return;
      for (let n = 1; n <= 5; n++) {
        if (!this.validateStep(n)) {
          if (n !== this.step) this.showStep(n);
          this.validateStep(n); // re-show errors after the step switch cleared the form error
          this.focusFirstError();
          return;
        }
      }

      if (this.photos.some((p) => p.status === 'processing' || p.status === 'uploading')) {
        this.setBusy(true, 'Finishing photo uploads...');
        await this.queue;
      }
      if (!this.photos.some((p) => p.status === 'done') || this.photos.some((p) => p.status === 'error')) {
        this.setBusy(false);
        this.showStep(1, { focus: true });
        this.validateStep(1);
        if (!this.photos.some((p) => p.status === 'error')) this.setError('photos', 'Your photos did not upload. Please try again.');
        return;
      }

      this.setBusy(true, 'Submitting...');
      this.formError('');
      const payload = this.buildPayload();
      let res = null;
      let data = null;
      try {
        res = await fetch(`${this.hof.proxy}/submit`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        try { data = await res.json(); } catch (e) { data = null; }
      } catch (e) {
        this.setBusy(false);
        this.resetTurnstile();
        this.formError('We could not reach the server. Check your connection and try again. Your answers are saved.');
        return;
      }

      if (res.ok && data && data.ok) {
        this.onSuccess();
        return;
      }

      this.setBusy(false);
      this.resetTurnstile();
      const msg = (data && data.error) ||
        (res.status === 429 ? "You've sent a lot of catches in a short time. Please try again later." : 'Something went wrong. Please try again.');
      const field = data && data.field ? String(data.field) : '';
      const step = FIELD_STEPS[field];
      if (step) {
        const slot = ERROR_ALIASES[field] || field;
        this.showStep(step);
        if (this.errorBox(slot)) {
          this.setError(slot, msg);
          this.focusFirstError();
        } else {
          this.formError(msg);
        }
      } else {
        this.formError(msg);
      }
    }

    onSuccess() {
      this.done = true;
      this.submitting = false;
      local.remove(DRAFT_KEY);
      this.steps.forEach((s) => { s.hidden = true; });
      qs('[data-hof-draft-notice]', this.el).hidden = true;
      qs('[data-hof-form-footer]', this.el).hidden = true;
      qs('[data-hof-progress]', this.el).hidden = true;
      const next = qs('[data-hof-next]', this.el);
      next.disabled = false;
      qs('[data-hof-back]', this.el).disabled = false;
      const success = qs('[data-hof-success]', this.el);
      success.hidden = false;
      this.body.scrollTop = 0;
      this.modal.focus(success);
    }
  }

  /* ================================================================
   * Boot
   * ============================================================== */
  function init(scope) {
    qsa('[data-hof-root]', scope).forEach((root) => {
      if (root.hofInstance) return;
      try {
        new HallOfFame(root); // eslint-disable-line no-new
      } catch (err) {
        console.error('[Hall of Fame] init failed', err);
      }
    });
  }

  captureSource();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    qsa('[data-hof-root]', e.target).forEach((root) => { if (root.hofInstance) root.hofInstance.destroy(); });
  });
  // Share text and links, exposed for worker/test/theme-share.test.ts.
  window.PBHallOfFame = Object.assign(window.PBHallOfFame || {}, { share: { shareMessage, shareText, shareLinks } });
})();
